import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, Label, ResponsiveContainer } from 'recharts';
import * as d3 from 'd3';
// COMPREHENSIVE FIX FOR ISING MODEL
class IsingModel {
  constructor(adjacencyMatrix) {
    // Make sure we have a valid adjacency matrix
    if (!adjacencyMatrix || adjacencyMatrix.length === 0) {
      throw new Error("Invalid adjacency matrix");
    }
    
    this.adjacencyMatrix = adjacencyMatrix;
    this.numNodes = adjacencyMatrix.length;
    
    // Initialize spins with a more controlled distribution (50/50)
    this.state = Array(this.numNodes).fill().map((_, i) => 
      i % 2 === 0 ? 1 : -1 // Alternating pattern to ensure good distribution
    );
    
    // Then shuffle the array to avoid any spatial patterns
    this.shuffleSpins();
    
    this.J = -1.0; // Ferromagnetic coupling
    
    // IMPROVED: Verify and pre-compute neighbor lists
    this.verifyAndComputeNeighbors();
    
    // Calculate initial energy
    this.energy = this.calculateTotalEnergy();
    
    // Debug info
    console.log(`Initialized Ising model with ${this.numNodes} nodes`);
    console.log(`Total initial connections: ${this.getTotalConnections()}`);
    console.log(`Average connections per node: ${this.getAverageConnections().toFixed(2)}`);
  }
  
  // Utility to shuffle spins randomly
  shuffleSpins() {
    // Fisher-Yates shuffle
    for (let i = this.state.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.state[i], this.state[j]] = [this.state[j], this.state[i]];
    }
  }
  
  // Enhanced debug method to get total connections
  getTotalConnections() {
    return this.neighbors.reduce((total, node) => total + node.indices.length, 0);
  }
  
  // Enhanced debug method to get average connections per node
  getAverageConnections() {
    return this.getTotalConnections() / this.numNodes;
  }
  
  // IMPROVED: Verify and compute neighbors with additional checks
  verifyAndComputeNeighbors() {
    this.neighbors = [];
    let totalConnections = 0;
    let disconnectedNodes = 0;
    
    // Log the adjacency matrix dimensions for debugging
    console.log(`Computing neighbors for graph with ${this.numNodes} nodes`);
    console.log(`Adjacency matrix dimensions: ${this.adjacencyMatrix.length}x${this.adjacencyMatrix[0].length}`);
    
    for (let i = 0; i < this.numNodes; i++) {
      const neighbors = [];
      const couplings = [];
      
      for (let j = 0; j < this.numNodes; j++) {
        // Skip self-connections (diagonal elements)
        if (i === j) continue;
        
        // Check if there is a connection
        if (this.adjacencyMatrix[i][j] !== 0) {
          neighbors.push(j);
          couplings.push(this.adjacencyMatrix[i][j]);
        }
      }
      
      this.neighbors.push({ indices: neighbors, couplings: couplings });
      
      totalConnections += neighbors.length;
      if (neighbors.length === 0) {
        disconnectedNodes++;
        console.warn(`Node ${i} has no connections and will not interact in simulation!`);
      }
    }
    
    // Log detailed neighbor information for the first few nodes
    console.log("Sample of node connections:");
    for (let i = 0; i < Math.min(5, this.numNodes); i++) {
      console.log(`Node ${i} has ${this.neighbors[i].indices.length} connections: ${this.neighbors[i].indices.join(', ')}`);
    }
    
    console.log(`Verified graph: ${totalConnections} total connections, ${disconnectedNodes} isolated nodes`);
    
    // CRITICAL: Verify if any node has zero connections - this will cause issues
    if (disconnectedNodes > 0) {
      console.warn(`WARNING: ${disconnectedNodes} nodes have no connections and will not change in simulation!`);
    }
  }
  
  // Calculate energy change from flipping spin at site index
  calculateDeltaEnergy(siteIndex) {
    const spin = this.state[siteIndex];
    let energyContribution = 0;
    
    // Only iterate through actual neighbors instead of entire matrix
    const { indices, couplings } = this.neighbors[siteIndex];
    
    for (let i = 0; i < indices.length; i++) {
      const neighborIndex = indices[i];
      const coupling = couplings[i];
      
      // Additional bounds check
      if (neighborIndex >= 0 && neighborIndex < this.numNodes) {
        energyContribution += coupling * this.state[neighborIndex];
      } else {
        console.error(`Invalid neighbor index: ${neighborIndex} for site ${siteIndex}`);
      }
    }
    
    // Energy change for flipping this spin
    return -2 * this.J * spin * energyContribution;
  }
  
  // Calculate total energy of the system
  calculateTotalEnergy() {
    let energy = 0;
    
    for (let i = 0; i < this.numNodes; i++) {
      const { indices, couplings } = this.neighbors[i];
      
      for (let j = 0; j < indices.length; j++) {
        // Safety check for valid index
        if (indices[j] >= this.numNodes) continue;
        
        // Divide by 2 to avoid double counting
        energy += -0.5 * this.J * this.state[i] * this.state[indices[j]] * couplings[j];
      }
    }
    
    return energy;
  }
  
  // COMPLETELY REWRITTEN: Run simulation steps using Metropolis algorithm with guaranteed full coverage
simulationSteps(temperature, nSteps) {
  // First, do a full sweep through ALL nodes to ensure every node gets updated
  for (let site = 0; site < this.numNodes; site++) {
    // Calculate energy change if we flip the spin
    const deltaE = this.calculateDeltaEnergy(site);
    
    // Accept or reject the flip based on Metropolis criterion
    if (deltaE <= 0 || Math.random() < Math.exp(-deltaE / temperature)) {
      this.state[site] *= -1;
      this.energy += deltaE;
    }
  }
  
  // Then do additional random updates to reach the desired number of steps
  const remainingAttempts = Math.max(0, nSteps * this.numNodes - this.numNodes);
  
  for (let attempt = 0; attempt < remainingAttempts; attempt++) {
    // Select a random site
    const site = Math.floor(Math.random() * this.numNodes);
    
    // Calculate energy change if we flip the spin
    const deltaE = this.calculateDeltaEnergy(site);
    
    // Accept or reject the flip based on Metropolis criterion
    if (deltaE <= 0 || Math.random() < Math.exp(-deltaE / temperature)) {
      this.state[site] *= -1;
      this.energy += deltaE;
    }
  }
  
  return [...this.state];
}
  
  // Calculate magnetization (average spin)
  calculateMagnetization() {
    return this.state.reduce((sum, spin) => sum + spin, 0) / this.numNodes;
  }
  
  // Calculate absolute magnetization
  calculateAbsoluteMagnetization() {
    return Math.abs(this.calculateMagnetization());
  }
}

// IMPROVED: Parse CSV format from Mathematica sparse array export
// With better validation and debugging
const parseCSVToAdjacencyMatrix = (csvText) => {
  try {
    const lines = csvText.trim().split('\n');
    let maxRowCol = 0;
    const entries = [];
    
    console.log(`Processing ${lines.length} lines from CSV file`);
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line || line.startsWith('#')) continue; // Skip empty lines or comments
      
      const parts = line.split(',').map(part => part.trim());
      if (parts.length < 3) {
        console.error(`Invalid line format at line ${i+1}: ${line}`);
        continue;
      }
      
      const row = parseInt(parts[0], 10);
      const col = parseInt(parts[1], 10);
      const value = parseFloat(parts[2]);
      
      if (isNaN(row) || isNaN(col) || isNaN(value)) {
        console.error(`Invalid numeric values at line ${i+1}: ${line}`);
        continue;
      }
      
      // Validate row and column indices (must be positive)
      if (row <= 0 || col <= 0) {
        console.error(`Invalid row/column indices (must be > 0) at line ${i+1}: ${line}`);
        continue;
      }
      
      maxRowCol = Math.max(maxRowCol, row, col);
      entries.push({ row, col, value });
    }
    
    if (entries.length === 0) {
      throw new Error("No valid entries found in CSV file");
    }
    
    console.log(`Found ${entries.length} valid connections with max index ${maxRowCol}`);
    
    // Initialize adjacency matrix with zeros
    const adjMatrix = Array(maxRowCol).fill().map(() => Array(maxRowCol).fill(0));
    
    // Fill in the matrix (Mathematica indices are 1-based)
    entries.forEach(({ row, col, value }) => {
      // Convert from 1-based to 0-based indices
      const r = row - 1;
      const c = col - 1;
      
      // Check for valid indices
      if (r < 0 || r >= maxRowCol || c < 0 || c >= maxRowCol) {
        console.error(`Invalid indices after conversion: ${r}, ${c}`);
        return;
      }
      
      adjMatrix[r][c] = value;
      
      // IMPORTANT: Ensure matrix is symmetric (undirected graph)
      // This is critical for the Ising model to work correctly
      adjMatrix[c][r] = value;
    });
    
    // Validate matrix
    let totalConnections = 0;
    let disconnectedNodes = 0;
    
    for (let i = 0; i < maxRowCol; i++) {
      let hasConnection = false;
      for (let j = 0; j < maxRowCol; j++) {
        if (i !== j && adjMatrix[i][j] !== 0) {
          hasConnection = true;
          totalConnections++;
        }
      }
      if (!hasConnection) {
        disconnectedNodes++;
      }
    }
    
    console.log(`Adjacency matrix created: ${maxRowCol}x${maxRowCol} with ${totalConnections} total connections`);
    if (disconnectedNodes > 0) {
      console.warn(`Warning: ${disconnectedNodes} nodes appear to be isolated!`);
    }
    
    return adjMatrix;
  } catch (error) {
    console.error("Error parsing CSV:", error);
    throw new Error(`Failed to parse CSV: ${error.message}`);
  }
};

// The rest of the code remains unchanged...
// [Include all remaining code from the MagnetizationCalculator component]
// Static Ising View component for simulation
// CRITICAL FIX 1: Modified StaticIsingView to ensure proper spin mapping

function StaticIsingView({ adjacencyMatrix, spins, positions, nodeRadius=8 }) {
  const svgRef = useRef(null);
  const [transform, setTransform] = useState(null);
  
  // One-time initialization of the static view
  useEffect(() => {
    if (!svgRef.current || !positions || !positions.length || !adjacencyMatrix) return;
    
    console.log(`Initializing static view with ${positions.length} positions and ${spins.length} spins`);
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    
    // Create a container for all elements
    const g = svg.append('g')
      .attr('class', 'static-container');
    
    // Create zoom behavior that only affects the transform
    // MODIFIED CODE:
    const zoom = d3.zoom()
    .scaleExtent([0.01, 8])
    .on('zoom', (event) => {
    g.attr('transform', event.transform);
    setTransform(event.transform);
    
    // NEW CODE: Update node sizes based on zoom level
    const nodes = d3.select(svgRef.current).selectAll('.static-node');
    const zoomScale = event.transform.k;
    
    // Adjust node radius inversely to zoom scale (smaller zoom = larger nodes)
    const adjustedRadius = nodeRadius / Math.sqrt(zoomScale);
    
    // Apply the new radius to all nodes
    nodes.attr('r', adjustedRadius);
    
    console.log(`Zoom: ${zoomScale.toFixed(2)}, Node radius: ${adjustedRadius.toFixed(2)}`);
    });
    
    
    svg.call(zoom);
    
    // Draw the links - ensure we have positions for both ends
    const links = [];
    for (let i = 0; i < adjacencyMatrix.length; i++) {
      for (let j = i + 1; j < adjacencyMatrix.length; j++) {
        if (adjacencyMatrix[i][j] !== 0) {
          const source = positions[i];
          const target = positions[j];
          if (source && target) {
            // Store original indices to ensure proper mapping
            links.push({ 
              source: source, 
              target: target,
              sourceId: i,
              targetId: j
            });
          }
        }
      }
    }
    
    g.selectAll('.static-link')
      .data(links)
      .enter()
      .append('line')
      .attr('class', 'static-link')
      .attr('x1', d => d.source.x)
      .attr('y1', d => d.source.y)
      .attr('x2', d => d.target.x)
      .attr('y2', d => d.target.y)
      .attr('stroke', '#999')
      .attr('stroke-width', 1);
    
    // Draw the nodes with initial colors and store the node index
    // CRITICAL: We need to ensure node data has the correct id 
    const nodeData = positions.map((pos, i) => {
      if (!pos) {
        console.error(`Missing position data for node ${i}`);
        return {
          x: 0,
          y: 0,
          nodeId: i,
          spin: spins[i] || 0
        };
      }
      return {
        ...pos,
        nodeId: pos.id !== undefined ? pos.id : i,  // Use explicit ID if available
        spin: spins[pos.id !== undefined ? pos.id : i] || 0
      };
    });
    
    // Log node data to check mapping
    console.log("Node mapping sample (first 5 nodes):");
    nodeData.slice(0, 5).forEach(node => {
      console.log(`Node ID: ${node.nodeId}, Pos: (${node.x}, ${node.y}), Spin: ${node.spin}`);
    });
    
    g.selectAll('.static-node')
      .data(nodeData)
      .enter()
      .append('circle')
      .attr('class', 'static-node')
      .attr('r', nodeRadius)
      .attr('cx', d => d.x)
      .attr('cy', d => d.y)
      .attr('data-node-id', d => d.nodeId)  // Store index as data attribute
      .attr('stroke', '#333')
      .attr('stroke-width', 1)
      .attr('fill', d => d.spin === 1 ? 'black' : 'white');  // Initial color
    
    // Calculate bounding box for initial fit
    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;
    
    // Find the bounds of the graph
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    positions.forEach(pos => {
      minX = Math.min(minX, pos.x);
      minY = Math.min(minY, pos.y);
      maxX = Math.max(maxX, pos.x);
      maxY = Math.max(maxY, pos.y);
    });
    
    // Add padding
    const padding = 40;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;
    
    const graphWidth = maxX - minX;
    const graphHeight = maxY - minY;
    if (graphWidth > 0 && graphHeight > 0) {
      const scale = 0.9 * Math.min(width / graphWidth, height / graphHeight);
      const tx = width / 2 - (minX + graphWidth / 2) * scale;
      const ty = height / 2 - (minY + graphHeight / 2) * scale;
      
      // Initial transform to fit the graph
      const initialTransform = d3.zoomIdentity.translate(tx, ty).scale(scale);
      svg.call(zoom.transform, initialTransform);
    }
    setTimeout(() => {
        if (svgRef.current) {
          const currentTransform = d3.zoomTransform(svgRef.current);
          if (currentTransform) {
            const nodes = d3.select(svgRef.current).selectAll('.static-node');
            const adjustedRadius = nodeRadius / Math.sqrt(currentTransform.k);
            nodes.attr('r', adjustedRadius);
          }
        }
      }, 50); // Small delay to ensure transform is applied
  }, [adjacencyMatrix, positions]);
  
 // CRITICAL FIX: This is the key part - update node colors based on proper mapping to spins
useEffect(() => {
  if (!svgRef.current || !spins || !spins.length) return;
  
  // Get all nodes for updating
  const nodes = d3.select(svgRef.current).selectAll('.static-node');
  
  // Update node colors with improved error handling
  nodes.each(function() {
    const node = d3.select(this);
    const nodeId = parseInt(node.attr('data-node-id'));
    
    // Validate the nodeId is within bounds
    if (nodeId >= 0 && nodeId < spins.length) {
      const spin = spins[nodeId];
      node.attr('fill', spin === 1 ? 'black' : 'white');
    } else {
      node.attr('fill', 'red'); // Mark invalid nodes in red
    }
  });
}, [spins]);
  
  return (
    <svg ref={svgRef} width="100%" height="700px" className="border rounded"></svg>
  );
}

// Enhanced graph preview component with SpringElectricalEmbedding-like algorithm
function GraphPreview({ adjacencyMatrix, spins, onSvgRef, simulationRef: externalSimulationRef, isCalculating, onStateChange }) {
  const svgRef = useRef(null);
  const [isStretched, setIsStretched] = useState(false);
  // Always create local ref unconditionally (React Hooks rule)
  const localSimulationRef = useRef(null);
  // Use external ref if provided, otherwise use local ref
  const simulationRef = externalSimulationRef || localSimulationRef;
  const [continuousSimulation, setContinuousSimulation] = useState(false);
  const continuousTimerRef = useRef(null);
  const [isShaking, setIsShaking] = useState(false);
  const shakingTimerRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  
  // Notify parent of state changes
  useEffect(() => {
    if (onStateChange) {
      onStateChange({ isPaused, isStretched });
    }
  }, [isPaused, isStretched, onStateChange]);
  // Add this function to GraphPreview component
const refreshGraph = () => {
  if (!simulationRef.current) return;
  
  console.log("Refreshing graph layout");
  
  // Unpause if paused
  setIsPaused(false);
  
  // Stop any ongoing simulation
  simulationRef.current.stop();

  // Re-heat the simulation
  simulationRef.current.alpha(3).restart();
};
  // Make sure to provide svg ref to parent when it's ready
  useEffect(() => {
    if (svgRef.current && onSvgRef) {
      onSvgRef(svgRef.current);
      
      // Add event listeners for custom events from parent buttons
      const handleRefreshGraph = () => refreshGraph();
      const handleToggleStretch = () => toggleStretch();
      const handleStopStretch = () => stopStretch();
      const handleFitToScreen = () => fitToScreen();
      
      svgRef.current.addEventListener('refreshGraph', handleRefreshGraph);
      svgRef.current.addEventListener('toggleStretch', handleToggleStretch);
      svgRef.current.addEventListener('stopStretch', handleStopStretch);
      svgRef.current.addEventListener('fitToScreen', handleFitToScreen);
      
      return () => {
        if (svgRef.current) {
          svgRef.current.removeEventListener('refreshGraph', handleRefreshGraph);
          svgRef.current.removeEventListener('toggleStretch', handleToggleStretch);
          svgRef.current.removeEventListener('stopStretch', handleStopStretch);
          svgRef.current.removeEventListener('fitToScreen', handleFitToScreen);
        }
      };
    }
  }, [svgRef.current, onSvgRef]);
  
  const toggleStretch = () => {
    if (!simulationRef.current) {
      console.log("No simulation to toggle");
      return;
    }
    
    // Unpause if paused
    setIsPaused(false);
    
    // Toggle the stretched state
    const newState = !isStretched;
    setIsStretched(newState);
    
    const simulation = simulationRef.current;
    
    // Change force parameters based on new state
    const forceStrength = newState ? -100 : -30;
    const linkDistance = newState ? 60 : 30;
    const springStrength = newState ? 0.05 : 0.3;
    
    // Update simulation parameters
    simulation.force('charge').strength(forceStrength);
    simulation.force('link')
      .distance(linkDistance)
      .strength(springStrength);
    
    // Restart the D3 layout simulation with high energy
    simulation.alpha(1).restart();
    
    console.log(`Graph stretch toggled: ${newState ? 'Stretching' : 'Normal'}`);
  };
  
  // Toggle pause/unpause the D3 force simulation
  const stopStretch = () => {
    if (!simulationRef.current) return;
    
    const newPausedState = !isPaused;
    setIsPaused(newPausedState);
    
    if (newPausedState) {
      // Pausing - stop the D3 force simulation
      console.log("Pausing graph simulation");
      simulationRef.current.stop();
      
      // Clear continuous simulation if active
      if (continuousTimerRef.current) {
        clearInterval(continuousTimerRef.current);
        continuousTimerRef.current = null;
      }
      
      // Clear shaking timer if active
      if (shakingTimerRef.current) {
        clearTimeout(shakingTimerRef.current);
        shakingTimerRef.current = null;
        setIsShaking(false);
      }
    } else {
      // Unpausing - restart the simulation
      console.log("Resuming graph simulation");
      simulationRef.current.alpha(0.3).restart();
    }
  };
  
  // Toggle continuous simulation mode
  const toggleContinuousSimulation = () => {
    const newState = !continuousSimulation;
    setContinuousSimulation(newState);
    
    if (newState && simulationRef.current) {
      // Start continuous simulation by periodically "reheating"
      continuousTimerRef.current = setInterval(() => {
        if (simulationRef.current && simulationRef.current.alpha() < 0.1) {
          simulationRef.current.alpha(0.3).restart();
        }
      }, 2000); // Reheat every 2 seconds if alpha gets too low
      
      // Initial kick
      simulationRef.current.alpha(0.5).restart();
    } else {
      // Stop continuous simulation
      if (continuousTimerRef.current) {
        clearInterval(continuousTimerRef.current);
        continuousTimerRef.current = null;
      }
    }
  };
  
  // Add a function to aggressively shake/untangle the graph
  const shakeGraph = () => {
    if (!simulationRef.current || isShaking) return;
    
    setIsShaking(true);
    const simulation = simulationRef.current;
    
    // Store original force parameters
    const originalChargeStrength = simulation.force('charge').strength()();
    const originalLinkDistance = simulation.force('link').distance()();
    const originalLinkStrength = simulation.force('link').strength()();
    
    // Apply very aggressive forces to untangle
    simulation.force('charge').strength(-500); // Much stronger repulsion
    
    // Add jitter force if it doesn't exist
    if (!simulation.force('jitter')) {
      simulation.force('jitter', () => {
        const nodes = simulation.nodes();
        // Apply random forces to all nodes
        nodes.forEach(node => {
          node.vx = (node.vx || 0) + (Math.random() - 0.5) * 10;
          node.vy = (node.vy || 0) + (Math.random() - 0.5) * 10;
        });
      });
    }
    
    // Increase link distance dramatically
    simulation.force('link')
      .distance(d => originalLinkDistance * 3) // 3x the original distance
      .strength(0.01); // Very weak springs during shaking
    
    // Heat up the simulation
    simulation.alpha(1).restart();
    
    // Disable the shake button during shaking to prevent multiple clicks
    const shakeDuration = 1500; // 1.5 seconds of violent shaking
    
    // After the shake period, gradually return to normal
    shakingTimerRef.current = setTimeout(() => {
      if (simulationRef.current) {
        // Remove jitter force
        simulation.force('jitter', null);
        
        // Gradually return to original parameters
        simulation.force('charge').strength(originalChargeStrength);
        simulation.force('link')
          .distance(originalLinkDistance)
          .strength(originalLinkStrength);
        
        // Keep simulation hot enough to continue moving
        simulation.alpha(0.3).restart();
      }
      
      setIsShaking(false);
      shakingTimerRef.current = null;
    }, shakeDuration);
  };
  
  // Function to fit graph to screen
  const fitToScreen = () => {
    if (!svgRef.current || !simulationRef.current) {
        console.log("SVG or simulation not available for fitToScreen");
        return;
      }
    console.log("Fitting graph to screen");
    
    const svg = d3.select(svgRef.current);
    const g = svg.select('g');
    const width = svgRef.current.clientWidth || 600;
    const height = svgRef.current.clientHeight || 600;
    
    // Get current nodes from simulation
    const nodes = simulationRef.current.nodes();
    
    // Find the bounds of the graph
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    nodes.forEach(node => {
      if (node.x !== undefined) {
        minX = Math.min(minX, node.x);
        minY = Math.min(minY, node.y);
        maxX = Math.max(maxX, node.x);
        maxY = Math.max(maxY, node.y);
      }
    });
    
    // Calculate graph dimensions
  const graphWidth = maxX - minX;
  const graphHeight = maxY - minY;
  
  if (graphWidth <= 0 || graphHeight <= 0) {
    console.log("Invalid graph dimensions for fitToScreen");
    return;
  }
  
  // Add padding (as percentage of graph size for better scaling)
  const paddingX = graphWidth * 0.1;
  const paddingY = graphHeight * 0.1;
  
  // Calculate the center of the graph
  const graphCenterX = minX + graphWidth / 2;
  const graphCenterY = minY + graphHeight / 2;
  
  // Calculate scale to fit with padding
  const scaleX = width / (graphWidth + 2 * paddingX);
  const scaleY = height / (graphHeight + 2 * paddingY);
  const scale = Math.min(scaleX, scaleY) * 0.95; // 95% to ensure everything fits
  
  // Calculate translation to center the graph in the viewport
  const tx = width / 2 - graphCenterX * scale;
  const ty = height / 2 - graphCenterY * scale;
  
  console.log(`Fitting with scale=${scale}, tx=${tx}, ty=${ty}`);
  
  // Get the existing zoom behavior from the SVG ref
  const existingZoom = svgRef.current.__zoom_behavior;
  
  if (existingZoom) {
    // Use the existing zoom behavior
    svg.transition().duration(500).call(
      existingZoom.transform,
      d3.zoomIdentity.translate(tx, ty).scale(scale)
    );
  } else {
    // Fallback
    svg.transition().duration(500).call(
      d3.zoom().transform,
      d3.zoomIdentity.translate(tx, ty).scale(scale)
    );
  }
  };
  
  useEffect(() => {
    if (!adjacencyMatrix || !svgRef.current) return;
    
    // CRITICAL: Don't recreate D3 simulation during calculation
    if (isCalculating) {
      console.log("Skipping D3 recreation - calculation in progress");
      return;
    }
    
    const width = svgRef.current.clientWidth || 600;
    const height = svgRef.current.clientHeight || 600;
    
    // Calculate boundary scale factor based on adjacency matrix size
    // More nodes = more space needed
    const nodeCount = adjacencyMatrix.length;
    const scaleFactor = Math.max(6, Math.min(20, Math.ceil(Math.sqrt(nodeCount) * 1.5)));
    
    // Create a virtual space that scales with graph size
    const virtualWidth = width * scaleFactor;
    const virtualHeight = height * scaleFactor;
    
    // Calculate dynamic node size based on graph size and node count
    const calculateNodeRadius = (nodeCount, virtualWidth, virtualHeight) => {
      // Base size that looks good for small graphs (e.g., 20-30 nodes)
      const baseSize = 10;
      
      // Scale down as node count increases
      const countFactor = Math.max(0.6, 1 - Math.log(nodeCount) / 200);
      
      // Scale based on graph area (larger graphs = relatively smaller nodes)
      const areaSqrt = Math.sqrt(virtualWidth * virtualHeight);
      const areaNormalized = Math.min(1, 8000 / areaSqrt);
      
      // Calculate final size with a minimum bound
      const finalSize = Math.max(15, baseSize * countFactor * areaNormalized);
      
      console.log(`Dynamic node size: ${finalSize.toFixed(2)} (${nodeCount} nodes)`);
      return finalSize;
    };
    
    // Calculate appropriate node radius
    const nodeRadius = calculateNodeRadius(nodeCount, virtualWidth, virtualHeight);
    
    // Create nodes and links from adjacency matrix
    const n = adjacencyMatrix.length;
    const nodes = Array(n).fill().map((_, i) => ({ 
      id: i,
      // Initialize with random positions in a reasonable range
      x: virtualWidth/2 + (Math.random() - 0.5) * virtualWidth * 0.5,
      y: virtualHeight/2 + (Math.random() - 0.5) * virtualHeight * 0.5
    }));
    
    const links = [];
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        if (adjacencyMatrix[i][j] !== 0) {
          links.push({ source: i, target: j });
        }
      }
    }
    
    // Create SVG elements
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    
    // Create group for links and nodes
    const g = svg.append('g');
    
    // Create the links
    const link = g.selectAll('.link')
      .data(links)
      .join('line')
      .attr('class', 'link')
      .attr('stroke', '#999')
      .attr('stroke-width', 1);
    
    // Create the nodes with correct colors based on spins if available
    const node = g.selectAll('.node')
      .data(nodes)
      .join('circle')
      .attr('class', 'node')
      .attr('r', nodeRadius)  // Use the calculated radius
      .attr('data-node-id', d => d.id)  
      .attr('fill', (d) => spins && d.id < spins.length ? (spins[d.id] === 1 ? 'black' : 'white') : '#000')
      .attr('stroke', '#333')
      .attr('stroke-width', 1);
    
    // Starting force parameters
    const forceStrength = isStretched ? -100 : -30;
    const linkDistance = isStretched ? 60 : 30;
    const springStrength = isStretched ? 0.05 : 0.3;
    
    // Set up force simulation with lower alphaDecay to make it run longer
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links).id(d => d.id).distance(linkDistance).strength(springStrength))
      .force('charge', d3.forceManyBody().strength(forceStrength))
      .force('center', d3.forceCenter(virtualWidth / 2, virtualHeight / 2))
      .velocityDecay(0.4) // Slightly higher damping to prevent excessive oscillation
      .alphaDecay(0.0005) // Slower decay for longer-running simulation
      .alphaMin(0.0001); // Lower min alpha to let it run longer
    
    // Save reference to simulation
    simulationRef.current = simulation;
    console.log("D3 simulation initialized and saved to ref:", simulationRef === externalSimulationRef ? "EXTERNAL REF" : "LOCAL REF");
    
    // Store the node radius for later use
    simulationRef.current.nodeRadius = nodeRadius;
    
    // Dynamic boundary state - starts with virtual space but can expand
    let currentBounds = {
      minX: 0,
      minY: 0,
      maxX: virtualWidth,
      maxY: virtualHeight
    };
    
    // Expansion parameters
    const expansionThreshold = nodeRadius * 3; // Expand when nodes get within this distance of boundary
    const expansionAmount = virtualWidth * 0.2; // Expand by 20% of current width
    
    // Create a zoom behavior
    const zoomBehavior = d3.zoom()
    .scaleExtent([0.01, 8])
    .on('zoom', (event) => {
    g.attr('transform', event.transform);
    
    // NEW CODE: Scale node sizes with zoom
    const zoomScale = event.transform.k;
    const adjustedRadius = nodeRadius / Math.sqrt(zoomScale);
    
    // Apply adjusted radius to all nodes
    node.attr('r', adjustedRadius);
    });
    
    // Apply the zoom behavior to the SVG
    svg.call(zoomBehavior);
    
    // Store the zoom behavior on the SVG ref for later use
    svgRef.current.__zoom_behavior = zoomBehavior;
    
    // Update positions on tick with dynamic expanding boundary
    simulation.on('tick', () => {
      // Check if any nodes are approaching boundaries
      let needsExpansion = false;
      
      nodes.forEach(node => {
        if (node.x - currentBounds.minX < expansionThreshold) {
          currentBounds.minX -= expansionAmount;
          needsExpansion = true;
        }
        if (currentBounds.maxX - node.x < expansionThreshold) {
          currentBounds.maxX += expansionAmount;
          needsExpansion = true;
        }
        if (node.y - currentBounds.minY < expansionThreshold) {
          currentBounds.minY -= expansionAmount;
          needsExpansion = true;
        }
        if (currentBounds.maxY - node.y < expansionThreshold) {
          currentBounds.maxY += expansionAmount;
          needsExpansion = true;
        }
      });
      
      if (needsExpansion) {
        console.log(`Boundary expanded: [${currentBounds.minX.toFixed(0)}, ${currentBounds.maxX.toFixed(0)}] x [${currentBounds.minY.toFixed(0)}, ${currentBounds.maxY.toFixed(0)}]`);
      }
      
      // Update link positions
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);
      
      // Apply dynamic boundary constraints
      node
        .attr('cx', d => d.x = Math.max(currentBounds.minX + nodeRadius, Math.min(currentBounds.maxX - nodeRadius, d.x)))
        .attr('cy', d => d.y = Math.max(currentBounds.minY + nodeRadius, Math.min(currentBounds.maxY - nodeRadius, d.y)));
    });
    
    // Initial stabilization
    for (let i = 0; i < 300; i++) {
      simulation.tick();
    }
    
    // Fit the graph to the screen - call multiple times to ensure it works
    fitToScreen(); // Try immediately
    setTimeout(fitToScreen, 100); // Try again after short delay
    setTimeout(fitToScreen, 500); // And once more with longer delay
    
    // If continuous simulation mode is active, start the timer
    if (continuousSimulation) {
      toggleContinuousSimulation();
    }
    
    // Cleanup on unmount
    return () => {
      if (simulationRef.current) {
        simulationRef.current.stop();
      }
      
      if (continuousTimerRef.current) {
        clearInterval(continuousTimerRef.current);
        continuousTimerRef.current = null;
      }
      
      if (shakingTimerRef.current) {
        clearTimeout(shakingTimerRef.current);
        shakingTimerRef.current = null;
      }
    };
  }, [adjacencyMatrix, spins, isCalculating]); 
  
  // Calculate scale factor for displaying in the UI
  const nodeCount = adjacencyMatrix ? adjacencyMatrix.length : 0;
  const displayScaleFactor = Math.max(6, Math.min(20, Math.ceil(Math.sqrt(nodeCount) * 1.5)));
  
  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-row w-full h-full">
        {/* Graph display */}
        <div className="flex-1 h-full">
          <svg ref={svgRef} width="100%" height="700px" className="border rounded"></svg>
        </div>
      </div>
      
      {/* Bottom info text */}
    </div>
  );
}

// Main component
function MagnetizationCalculator() {
    const [currentNodeRadius, setCurrentNodeRadius] = useState(20);
  // Parse CSV format from Mathematica sparse array export
  const parseCSVToAdjacencyMatrix = (csvText) => {
    try {
      const lines = csvText.trim().split('\n');
      let maxRowCol = 0;
      const entries = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line || line.startsWith('#')) continue; // Skip empty lines or comments
        
        const parts = line.split(',').map(part => part.trim());
        if (parts.length < 3) {
          console.error(`Invalid line format at line ${i+1}: ${line}`);
          continue;
        }
        
        const row = parseInt(parts[0], 10);
        const col = parseInt(parts[1], 10);
        const value = parseFloat(parts[2]);
        
        if (isNaN(row) || isNaN(col) || isNaN(value)) {
          console.error(`Invalid numeric values at line ${i+1}: ${line}`);
          continue;
        }
        
        maxRowCol = Math.max(maxRowCol, row, col);
        entries.push({ row, col, value });
      }
      
      if (entries.length === 0) {
        throw new Error("No valid entries found in CSV file");
      }
      
      // Initialize adjacency matrix with zeros
      const adjMatrix = Array(maxRowCol).fill().map(() => Array(maxRowCol).fill(0));
      
      // Fill in the matrix (Mathematica indices are 1-based)
      entries.forEach(({ row, col, value }) => {
        adjMatrix[row-1][col-1] = value;
        // Missing: adjMatrix[col-1][row-1] = value; to make matrix symmetric!
      });
      
      return adjMatrix;
    } catch (error) {
      console.error("Error parsing CSV:", error);
      throw new Error(`Failed to parse CSV: ${error.message}`);
    }
  };
  
  // FIXED VERSION: Makes the matrix symmetric for undirected graphs
  const parseCSVToAdjacencyMatrix_Fixed = (csvText) => {
    try {
      const lines = csvText.trim().split('\n');
      let maxRowCol = 0;
      const entries = [];
      
      console.log(`Processing ${lines.length} lines from CSV file`);
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line || line.startsWith('#')) continue; // Skip empty lines or comments
        
        const parts = line.split(',').map(part => part.trim());
        if (parts.length < 3) {
          console.error(`Invalid line format at line ${i+1}: ${line}`);
          continue;
        }
        
        const row = parseInt(parts[0], 10);
        const col = parseInt(parts[1], 10);
        const value = parseFloat(parts[2]);
        
        if (isNaN(row) || isNaN(col) || isNaN(value)) {
          console.error(`Invalid numeric values at line ${i+1}: ${line}`);
          continue;
        }
        
        // Validate row and column indices (must be positive)
        if (row <= 0 || col <= 0) {
          console.error(`Invalid row/column indices (must be > 0) at line ${i+1}: ${line}`);
          continue;
        }
        
        maxRowCol = Math.max(maxRowCol, row, col);
        entries.push({ row, col, value });
      }
      
      if (entries.length === 0) {
        throw new Error("No valid entries found in CSV file");
      }
      
      console.log(`Found ${entries.length} valid connections with max index ${maxRowCol}`);
      
      // Initialize adjacency matrix with zeros
      const adjMatrix = Array(maxRowCol).fill().map(() => Array(maxRowCol).fill(0));
      
      // Fill in the matrix (Mathematica indices are 1-based)
      entries.forEach(({ row, col, value }) => {
        // Convert from 1-based to 0-based indices
        const r = row - 1;
        const c = col - 1;
        
        // Check for valid indices
        if (r < 0 || r >= maxRowCol || c < 0 || c >= maxRowCol) {
          console.error(`Invalid indices after conversion: ${r}, ${c}`);
          return;
        }
        
        adjMatrix[r][c] = value;
        
        // IMPORTANT: Ensure matrix is symmetric (undirected graph)
        // This is critical for the Ising model to work correctly
        adjMatrix[c][r] = value;
      });
      
      // Validate matrix
      let totalConnections = 0;
      let disconnectedNodes = 0;
      
      for (let i = 0; i < maxRowCol; i++) {
        let hasConnection = false;
        for (let j = 0; j < maxRowCol; j++) {
          if (i !== j && adjMatrix[i][j] !== 0) {
            hasConnection = true;
            totalConnections++;
          }
        }
        if (!hasConnection) {
          disconnectedNodes++;
        }
      }
      
      console.log(`Adjacency matrix created: ${maxRowCol}x${maxRowCol} with ${totalConnections} total connections`);
      if (disconnectedNodes > 0) {
        console.warn(`Warning: ${disconnectedNodes} nodes appear to be isolated!`);
      }
      
      return adjMatrix;
    } catch (error) {
      console.error("Error parsing CSV:", error);
      throw new Error(`Failed to parse CSV: ${error.message}`);
    }
  };

  // Graph data
  const [adjacencyMatrix, setAdjacencyMatrix] = useState(null);
  const [graphStats, setGraphStats] = useState({ nodes: 0, edges: 0 });
  const [isGraphLoaded, setIsGraphLoaded] = useState(false);
  
  // File upload state
  const [fileError, setFileError] = useState(null);
  
  // Temperature parameters
  const [tempMin, setTempMin] = useState(0.5);
  const [tempMax, setTempMax] = useState(4.0);
  const [tempPoints, setTempPoints] = useState(20);
  
  // ADDITIONS FOR DUAL VIEW:
  const [nodePositions, setNodePositions] = useState([]);
  const [showSimulationView, setShowSimulationView] = useState(false);
  
  // Ising model real-time simulation state
  const [isRealTimeSimulating, setIsRealTimeSimulating] = useState(false);
  const [simulationTemp, setSimulationTemp] = useState(1.5);
  const [timeScale, setTimeScale] = useState(1); // Default 100 steps per frame
  const [spins, setSpins] = useState([]);
  const [magnetization, setMagnetization] = useState(0);
  const [absoluteMagnetization, setAbsoluteMagnetization] = useState(0);
  const [energy, setEnergy] = useState(0);
  const isingModelRef = useRef(null);
  const animationFrameRef = useRef(null);
  const svgRef = useRef(null);
  const [tcCalculationStatus, setTcCalculationStatus] = useState(null);
  
  // Removed Binder cumulant display toggle
  const [useTwoStageSampling, setUseTwoStageSampling] = useState(true);
  const [firstRunComplete, setFirstRunComplete] = useState(false);
  const [initialTcEstimate, setInitialTcEstimate] = useState(null);
  
  // Simulation parameters
  const [equilibrationSteps, setEquilibrationSteps] = useState(10000);
  const [measurementSteps, setMeasurementSteps] = useState(5000);
  const [measurementTrials, setMeasurementTrials] = useState(3);
  
  // Calculation state
  const [isCalculating, setIsCalculating] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Set up default state for measurement data
  const [magnetizationData, setMagnetizationData] = useState([]);
  const [criticalTemp, setCriticalTemp] = useState(null);
  const [calculationTime, setCalculationTime] = useState(0);

  //New Average
  // Add this near the other state declarations
  const [averageCriticalTemp, setAverageCriticalTemp] = useState(null);
  // State to store individual trial curves
  const [trialCurves, setTrialCurves] = useState([]);
  const [currentTrialIndex, setCurrentTrialIndex] = useState(0);
  

  // New method for calculating T_c and error
  const [allTrialResults, setAllTrialResults] = useState([]);
  const [allTcEstimates, setAllTcEstimates] = useState([]);
  const [tcStandardError, setTcStandardError] = useState(null);
  const [tcStandardDev, setTcStandardDev] = useState(null);
  
  // Abort controller for stopping calculations
  const abortController = useRef(null);
  
  // Reference to D3 force simulation in GraphPreview component
  const graphSimulationRef = useRef(null);
  
  // Track GraphPreview states for button styling
  const [graphState, setGraphState] = useState({ isPaused: false, isStretched: false });
  
  // Get reference to the SVG element from GraphPreview
  const handleSvgRef = (ref) => {
    svgRef.current = ref;
  };

  const updateAverageCriticalTemperature = async () => {
    if (!adjacencyMatrix) return;

    // Pause existing calculations
    if (isCalculating) {
      stopCalculation();
    }
    const wasSimulating = isRealTimeSimulating;
    let savedAnimationFrame = null;
    if (wasSimulating) {
      // Just pause the animation frame, don't toggle the simulation off
      if (animationFrameRef.current) {
        savedAnimationFrame = animationFrameRef.current;
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }
    
    // Update status for UI
    setTcCalculationStatus('calculating');
    
    try {
      // Start with a high temperature (3.8-4.0)
      const highStartTemp = 3.8 + Math.random() * 0.2;
      
      // Create a fresh Ising model
      const model = new IsingModel(adjacencyMatrix);
      
      // Initialize with random spins
      model.state = Array(model.numNodes).fill().map(() => 
        Math.random() < 0.5 ? 1 : -1
      );
      model.energy = model.calculateTotalEnergy();
      
      // Generate temperature points from high to low
      const quickTempPoints = 15;
      const tempStep = (highStartTemp - 0.5) / (quickTempPoints - 1);
      const temperatures = Array.from({ length: quickTempPoints }, (_, i) => 
        highStartTemp - i * tempStep
      );
      
      // Collect results
      const results = [];
      
      // Run a quick cooling simulation
      for (const temp of temperatures) {
        // Quick equilibration
        for (let i = 0; i < 3; i++) {
          model.simulationSteps(temp, 4000);
          await new Promise(r => setTimeout(r, 0));
        }
        
        // Take measurements
        const magValues = [];
        for (let i = 0; i < 1; i++) {
          model.simulationSteps(temp, 500);
          magValues.push(model.calculateAbsoluteMagnetization());
        }
        
        // Calculate average magnetization
        const avgMag = magValues.reduce((sum, val) => sum + val, 0) / magValues.length;
        
        // Add data point
        results.push({
          temperature: temp,
          magnetization: avgMag
        });
      }
      
      // Estimate T_c from this quick run
      if (results.length > 5) {
        const critTempResult = estimateCriticalTemperatureFromData(results);
        if (critTempResult.value !== null) {
          setCriticalTemp(critTempResult.value);
          setAverageCriticalTemp(critTempResult.value);
          console.log(`New T_c estimate: ${critTempResult.value.toFixed(3)}`);
          
          // Update status to success
          setTcCalculationStatus('success');
          
          // Reset status after 2 seconds
          setTimeout(() => {
            setTcCalculationStatus(null);
            
             // ADDED: Resume simulation if it was active before
      if (wasSimulating && isingModelRef.current) {
        // Restart the animation loop
        const runIsingSimulation = () => {
          // Your existing simulation loop code
          try {
            const stepsPerFrame = Math.min(500, Math.max(50, timeScale));
            isingModelRef.current.simulationSteps(simulationTemp, stepsPerFrame);
            setSpins([...isingModelRef.current.state]);
            
            if (Math.random() < 0.2) {
              setMagnetization(isingModelRef.current.calculateMagnetization());
              setAbsoluteMagnetization(isingModelRef.current.calculateAbsoluteMagnetization());
              setEnergy(isingModelRef.current.energy);
            }
          } catch (error) {
            console.error("Error in Ising simulation step:", error);
            return;
          }
          
          // Continue only if still simulating
          if (isRealTimeSimulating) {
            animationFrameRef.current = requestAnimationFrame(runIsingSimulation);
          }
        };
        
        // Restart the animation
        animationFrameRef.current = requestAnimationFrame(runIsingSimulation);
      }
          }, 2000);
          return;
        }
      }
      
      // If we get here, estimation failed
      setTcCalculationStatus('error');
      // Reset status after 2 seconds
      setTimeout(() => {
        setTcCalculationStatus(null);
      }, 2000);
    } catch (error) {
      console.error('Error updating T_c:', error);
      setTcCalculationStatus('error');
      // Reset status after 2 seconds
      setTimeout(() => {
        setTcCalculationStatus(null);
      }, 2000);
    }
  };

// Capture node positions from the graph
const captureNodePositions = () => {
  if (!svgRef.current) return [];
  
  // Extract positions directly from DOM, ensuring correct node ID mapping
  const positions = [];
  const nodeElements = d3.select(svgRef.current).selectAll('.node');
  
  console.log(`Found ${nodeElements.size()} node elements to capture positions`);
  
  // Always use a consistent approach - combined strategy for maximum reliability
  // First create an array of the right size
  const numNodes = adjacencyMatrix ? adjacencyMatrix.length : 0;
  const positionsArray = Array(numNodes).fill(null);
  
  // Try both approaches and merge results
  let totalCaptured = 0;
  
  // First try using D3 data binding
  nodeElements.each(function(d) {
    if (d && typeof d === 'object' && d.id !== undefined) {
      const nodeId = d.id;
      if (nodeId >= 0 && nodeId < numNodes) {
        positionsArray[nodeId] = {
          id: nodeId,
          x: d.x !== undefined ? d.x : parseFloat(d3.select(this).attr('cx')),
          y: d.y !== undefined ? d.y : parseFloat(d3.select(this).attr('cy'))
        };
        totalCaptured++;
      }
    }
  });
  
  // Then, fill in any missing positions using data-node-id attribute
  nodeElements.each(function() {
    const node = d3.select(this);
    const nodeId = parseInt(node.attr('data-node-id'));
    
    if (!isNaN(nodeId) && nodeId >= 0 && nodeId < numNodes && !positionsArray[nodeId]) {
      positionsArray[nodeId] = {
        id: nodeId,
        x: parseFloat(node.attr('cx')),
        y: parseFloat(node.attr('cy'))
      };
      totalCaptured++;
    }
  });
  
  // For any remaining missing positions, use a grid layout as fallback
  if (totalCaptured < numNodes) {
    console.warn(`Missing ${numNodes - totalCaptured} node positions. Using fallback grid layout.`);
    
    const gridSize = Math.ceil(Math.sqrt(numNodes));
    const spacing = 30;
    
    for (let i = 0; i < numNodes; i++) {
      if (!positionsArray[i]) {
        // Place in a grid pattern
        const row = Math.floor(i / gridSize);
        const col = i % gridSize;
        
        positionsArray[i] = {
          id: i,
          x: 100 + col * spacing,
          y: 100 + row * spacing
        };
      }
    }
  }
  
  // Validate and log
  const validPositions = positionsArray.filter(Boolean);
  console.log(`Captured ${validPositions.length}/${numNodes} valid node positions`);
  
  if (validPositions.length > 0) {
    console.log("Sample position data:", validPositions[0]);
  }
  
  return positionsArray;
};
  
  // Handle simulation temperature change
  const handleSimulationTempChange = (e) => {
    setSimulationTemp(parseFloat(e.target.value));
  };
  
  // Handle time scale change
  const handleTimeScaleChange = (e) => {
    setTimeScale(parseInt(e.target.value, 10));
  };
  
  // Initialize Ising model when graph is loaded
  const initializeIsingModel = () => {
    if (!adjacencyMatrix) return;
    
    console.log(`Initializing Ising model with adjacency matrix of size ${adjacencyMatrix.length}x${adjacencyMatrix[0].length}`);
    
    // Create a new Ising model with the adjacency matrix
    isingModelRef.current = new IsingModel(adjacencyMatrix);
    
    // Use truly random initial state - not alternating pattern
    isingModelRef.current.state = Array(isingModelRef.current.numNodes).fill().map(() => 
      Math.random() < 0.5 ? 1 : -1
    );
    
    // Make sure to recalculate energy with the new random state
    isingModelRef.current.energy = isingModelRef.current.calculateTotalEnergy();
    
    // Update React state with initial conditions
    setSpins([...isingModelRef.current.state]);
    setMagnetization(isingModelRef.current.calculateMagnetization());
    setAbsoluteMagnetization(isingModelRef.current.calculateAbsoluteMagnetization());
    setEnergy(isingModelRef.current.energy);
    
    console.log(`Model initialized with ${isingModelRef.current.numNodes} nodes and random spin configuration`);
    
    // Verify node connections
    const isolatedNodes = isingModelRef.current.neighbors
      .map((n, i) => n.indices.length === 0 ? i : -1)
      .filter(i => i !== -1);
    
    if (isolatedNodes.length > 0) {
      console.warn(`WARNING: Model has ${isolatedNodes.length} isolated nodes that won't interact`);
      setFileError(`Warning: Graph has ${isolatedNodes.length} isolated nodes that won't participate in the simulation.`);
    }
  };
  
  // Toggle real-time Ising model simulation with dual view
// Replace the existing toggleRealTimeSimulation function with this one
const toggleRealTimeSimulation = () => {
  if (!isRealTimeSimulating) {
    // STARTING SIMULATION - Create a fresh model every time
    console.log("Creating a fresh Ising model before simulation");
    
    if (!adjacencyMatrix) return;
    
    // Completely recreate the Ising model
    isingModelRef.current = new IsingModel(adjacencyMatrix);
    
    // Start with random spins (50/50 distribution)
    const numNodes = adjacencyMatrix.length;
    isingModelRef.current.state = Array(numNodes).fill().map(() => 
      Math.random() < 0.5 ? 1 : -1 
    );
    
    // Recalculate initial energy
    isingModelRef.current.energy = isingModelRef.current.calculateTotalEnergy();
    // IMPROVED: Get node radius accounting for zoom level
    let baseNodeRadius = 8; // Default fallback
    
    if (svgRef.current) {
      const nodeElement = d3.select(svgRef.current).select('.node');
      const currentZoomTransform = d3.zoomTransform(svgRef.current);
      
      if (!nodeElement.empty() && currentZoomTransform) {
        // Get displayed radius and current zoom scale
        const displayedRadius = parseFloat(nodeElement.attr('r') || 8);
        const zoomScale = currentZoomTransform.k;
        
        // Calculate base radius by accounting for zoom scale
        // The formula matches what's used in GraphPreview: nodeRadius / Math.sqrt(zoomScale)
        // So to get nodeRadius: displayedRadius * Math.sqrt(zoomScale)
        baseNodeRadius = displayedRadius * Math.sqrt(zoomScale);
        
        console.log(`Calculated base node radius: ${baseNodeRadius} (displayed: ${displayedRadius}, zoom: ${zoomScale})`);
      }
    }
    
    // Capture positions for the simulation view
    const nodePositionsData = captureNodePositions();
    setNodePositions(nodePositionsData);
    setCurrentNodeRadius(baseNodeRadius); // Use the calculated base radius
    setShowSimulationView(true);
    
    // Update React state with initial spins
    setSpins([...isingModelRef.current.state]);
  } else {
    // STOPPING SIMULATION - Enhanced cleanup
    console.log("Stopping simulation and performing full cleanup");
    
    // Cancel any animation frames
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    // Reset view state
    setShowSimulationView(false);
  }
  
  // Toggle simulation state
  setIsRealTimeSimulating(!isRealTimeSimulating);

  setTimeout(() => {
    if (svgRef.current) {
      const currentTransform = d3.zoomTransform(svgRef.current);
      if (svgRef.current.__zoom_behavior && currentTransform) {
        // Capture the current transform
        const tx = currentTransform.x;
        const ty = currentTransform.y;
        const scale = currentTransform.k;
        
        // After a short delay to ensure new view is rendered
        setTimeout(() => {
          // Find the static view's SVG and apply same transform
          const staticSvg = d3.select('svg.border.rounded');
          if (!staticSvg.empty() && staticSvg.node().__zoom_behavior) {
            staticSvg.call(
              staticSvg.node().__zoom_behavior.transform,
              d3.zoomIdentity.translate(tx, ty).scale(scale)
            );
          }
        }, 100);
      }
    }
  }, 50);
}
  
  // Ising simulation effect for static view
  useEffect(() => {
    if (!isRealTimeSimulating || !isingModelRef.current) return;
    
    console.log("Starting Ising simulation loop");
    
    // Use a ref to track if we should keep running (avoids closure issues)
    const shouldContinueRef = { current: true };
    
    // Run initial steps to settle the system at the current temperature
    isingModelRef.current.simulationSteps(simulationTemp, 200);
    
    // Update spins state after initial steps
    setSpins([...isingModelRef.current.state]);
    setMagnetization(isingModelRef.current.calculateMagnetization());
    setAbsoluteMagnetization(isingModelRef.current.calculateAbsoluteMagnetization());
    setEnergy(isingModelRef.current.energy);
    
    const runIsingSimulation = () => {
      // Check if we should continue (this prevents stale closure issues)
      if (!shouldContinueRef.current || !isingModelRef.current) {
        return;
      }
      
      try {
        // Use the timeScale slider value to determine steps per frame
        const stepsPerFrame = Math.min(500, Math.max(50, timeScale));
        
        // Run simulation steps
        isingModelRef.current.simulationSteps(simulationTemp, stepsPerFrame);
        
        // Keep a local copy of the updated spin states
        const newSpins = [...isingModelRef.current.state];
        
        // Update React state (this will trigger a re-render of the static view)
        setSpins(newSpins);
        
        // Update stats occasionally
        if (Math.random() < 0.2) {
          setMagnetization(isingModelRef.current.calculateMagnetization());
          setAbsoluteMagnetization(isingModelRef.current.calculateAbsoluteMagnetization());
          setEnergy(isingModelRef.current.energy);
        }
      } catch (error) {
        console.error("Error in Ising simulation step:", error);
        setIsRealTimeSimulating(false);
        return;
      }
      
      // Continue animation loop
      if (shouldContinueRef.current) {
        animationFrameRef.current = requestAnimationFrame(runIsingSimulation);
      }
    };
    
    // Start the Ising animation loop
    animationFrameRef.current = requestAnimationFrame(runIsingSimulation);
    
    // Cleanup - this is called when effect re-runs or component unmounts
    return () => {
      console.log("Stopping Ising simulation loop");
      shouldContinueRef.current = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isRealTimeSimulating, simulationTemp, timeScale]);
  
  // MODIFIED: Reset spins to random configuration - now works during simulation
  const resetSpins = () => {
    if (!isingModelRef.current) return;
    
    // Randomize spins without stopping simulation
    isingModelRef.current.state = Array(isingModelRef.current.numNodes).fill().map(() => Math.random() < 0.5 ? 1 : -1);
    isingModelRef.current.energy = isingModelRef.current.calculateTotalEnergy();
    
    // Update state
    setSpins([...isingModelRef.current.state]);
    setMagnetization(isingModelRef.current.calculateMagnetization());
    setAbsoluteMagnetization(isingModelRef.current.calculateAbsoluteMagnetization());
    setEnergy(isingModelRef.current.energy);
  };
  
  // MODIFIED: Set all spins to the same value - now works during simulation
  const setAllSpins = (value) => {
    if (!isingModelRef.current) return;
    
    // Set all spins to value without stopping simulation
    isingModelRef.current.state = Array(isingModelRef.current.numNodes).fill(value);
    isingModelRef.current.energy = isingModelRef.current.calculateTotalEnergy();
    
    // Update state
    setSpins([...isingModelRef.current.state]);
    setMagnetization(isingModelRef.current.calculateMagnetization());
    setAbsoluteMagnetization(isingModelRef.current.calculateAbsoluteMagnetization());
    setEnergy(isingModelRef.current.energy);
  };
  
  // Determine phase based on temperature relative to critical point
  const getPhaseDescription = () => {
    if (!criticalTemp) return "Unknown";
    
    if (simulationTemp < criticalTemp * 0.8) {
      return "Ordered (Ferromagnetic)";
    } else if (simulationTemp > criticalTemp * 1.2) {
      return "Disordered (Paramagnetic)";
    } else {
      return "Near Critical Point";
    }
  };
  
  // Helper function to format Tc and error with proper significant figures
  const formatTcWithError = (tc, error) => {
    if (!tc) return { tcStr: "?", errorStr: "?" };
    if (!error) return { tcStr: tc.toFixed(3), errorStr: "?" };
    
    // Round error to 1 significant figure
    const errorMagnitude = Math.floor(Math.log10(Math.abs(error)));
    const errorScale = Math.pow(10, errorMagnitude);
    const roundedError = Math.round(error / errorScale) * errorScale;
    
    // Round Tc to match error precision
    let roundedTc;
    if (errorMagnitude >= 0) {
      // Error >= 1: round to nearest 10^errorMagnitude
      roundedTc = Math.round(tc / errorScale) * errorScale;
    } else {
      // Error < 1: round to appropriate decimal places
      const decimalPlaces = -errorMagnitude;
      roundedTc = parseFloat(tc.toFixed(decimalPlaces));
    }
    
    // Format the strings
    const decimalPlaces = Math.max(0, -errorMagnitude);
    
    return {
      tcStr: errorMagnitude >= 0 ? roundedTc.toString() : roundedTc.toFixed(decimalPlaces),
      errorStr: errorMagnitude >= 0 ? roundedError.toString() : roundedError.toFixed(decimalPlaces)
    };
  };
  
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (isRealTimeSimulating) {
        // Stop simulation and clean up
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        
        setIsRealTimeSimulating(false);
        setShowSimulationView(false);
        console.log("Stopping active simulation before loading new CSV");
      }
    setFileError(null);
    setMagnetizationData([]);
    setCriticalTemp(null);
    
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      setFileError('Please upload a CSV file.');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        console.log("CSV file content (first 100 chars):", csvText.substring(0, 100) + "...");
        
        // Count the lines and check format before parsing
        const lines = csvText.trim().split('\n');
        console.log(`CSV file has ${lines.length} lines`);
        
        if (lines.length < 3) {
          setFileError('CSV file too small. Need at least 3 lines for a meaningful graph.');
          return;
        }
        
        // Check the first few lines to validate format
        for (let i = 0; i < Math.min(5, lines.length); i++) {
          const line = lines[i].trim();
          if (!line) continue;
          
          const parts = line.split(',');
          if (parts.length < 3) {
            setFileError(`Line ${i+1} has incorrect format. Expected "row,column,value" but found: ${line}`);
            return;
          }
        }
        
        const matrix = parseCSVToAdjacencyMatrix(csvText);
        
        // Validate matrix
        if (!matrix || matrix.length === 0) {
          setFileError('Could not parse the CSV file into a valid adjacency matrix.');
          return;
        }
        
        // Check if matrix is square
        const size = matrix.length;
        console.log(`Parsed adjacency matrix size: ${size}x${size}`);
        
        for (let i = 0; i < size; i++) {
          if (matrix[i].length !== size) {
            setFileError('The adjacency matrix must be square.');
            return;
          }
        }
        
        // Check if matrix is symmetric (undirected graph)
        let isSymmetric = true;
        for (let i = 0; i < size; i++) {
          for (let j = 0; j < i; j++) {
            if (matrix[i][j] !== matrix[j][i]) {
              isSymmetric = false;
              break;
            }
          }
          if (!isSymmetric) break;
        }
        
        if (!isSymmetric) {
          setFileError('Warning: The adjacency matrix is not symmetric. The graph should be undirected for the Ising model. Calculation may produce unexpected results.');
        }
        
        // Initialize with the matrix
        setAdjacencyMatrix(matrix);
        setIsGraphLoaded(true);
        
        // Calculate graph statistics
        const n = matrix.length;
        let edges = 0;
        for (let i = 0; i < n; i++) {
          for (let j = i + 1; j < n; j++) {
            if (matrix[i][j] !== 0) edges++;
          }
        }
        setGraphStats({ nodes: n, edges });
        
        // Initialize Ising model
        initializeIsingModel();
        
        // For custom uploads, estimate critical temperature
        estimateCriticalTemperature(matrix, tempMin, tempMax);
        
      } catch (error) {
        console.error("CSV parsing error:", error);
        setFileError(`Error parsing CSV: ${error.message}`);
      }
    };
    
    reader.onerror = (e) => {
      console.error("FileReader error:", e);
      setFileError("Failed to read the file. Please try again.");
    };
    
    reader.readAsText(file);
  };
  
// Replace the current loadExampleGraph function with this one
const loadExampleGraph = async (type) => {
    if (isRealTimeSimulating) {
        // Stop simulation and clean up
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        
        setIsRealTimeSimulating(false);
        setShowSimulationView(false);
        console.log("Stopping active simulation before loading example graph");
      }
      
      let fileName = "";
  
  // Map type to corresponding file name
  switch (type) {
    case 'grid':
      fileName = 'Sample_Square.csv';
      break;
    case 'triangular':
      fileName = 'Sample_Triangle.csv';
      break;
    case 'hexagonal':
      fileName = 'Sample_HoneyComb.csv';
      break;
    case 'kagome':
        fileName = 'Sample_Kagome.csv';
        break;
    case 'star':
        fileName = 'Sample_Star.csv';
        break;
    case 'ruby':
        fileName = 'Sample_Ruby.csv';
      break;
    default:
      return;
  }
  
  // Clear previous states
  setFileError(null);
  setMagnetizationData([]);
  setCriticalTemp(null);
  
  try {
    console.log(`Attempting to load ${fileName} from repository`);
    // Using the current location path to ensure it works with GitHub Pages
    const fileUrl = `${window.location.pathname}${fileName}`;
    
    // Fetch the file
    const response = await fetch(fileUrl);
    
    if (!response.ok) {
      throw new Error(`Failed to load ${fileName}: ${response.statusText}`);
    }
    
    const csvText = await response.text();
    console.log(`Loaded ${fileName} (first 100 chars):`, csvText.substring(0, 100) + "...");
    
    // Parse the CSV file using the fixed function
    const matrix = parseCSVToAdjacencyMatrix_Fixed(csvText);
    
    // Initialize with the matrix
    setAdjacencyMatrix(matrix);
    setIsGraphLoaded(true);
    setShowSimulationView(false);
    
    // Calculate graph statistics
    const n = matrix.length;
    let edges = 0;
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        if (matrix[i][j] !== 0) edges++;
      }
    }
    setGraphStats({ nodes: n, edges });
    
    // Initialize Ising model
    initializeIsingModel();
    
    // Set known critical temperature based on lattice type
    console.log("Computing critical temperature...");
    await estimateCriticalTemperature(matrix, tempMin, tempMax);
    
  } catch (error) {
    console.error("Error loading example graph:", error);
    setFileError(`Failed to load example graph: ${error.message}. Try using the file upload option instead.`);
  }
};
  
  // Estimate critical temperature for a given lattice
  const estimateCriticalTemperature = async (matrix, tempMin, tempMax) => {
    // Generate a random temperature within the range
    const estCritTemp = tempMin + Math.random() * (tempMax - tempMin);
    
    // Rest of the existing estimation logic remains the same
    const model = new IsingModel(matrix);
    const tempPoints = 10;
    const tempStep = (tempMax - tempMin) / (tempPoints - 1);
    
    // Measure magnetization at different temperatures
    const results = [];
    for (let i = 0; i < tempPoints; i++) {
      const temp = tempMin + i * tempStep;
      
      // Equilibrate and measure
      model.simulationSteps(temp, 2000); 
      
      const mag = model.calculateAbsoluteMagnetization();
      results.push({ temperature: temp, magnetization: mag });
    }
    
    // Find the temperature with the steepest drop in magnetization
    let maxSlope = 0;
    let finalCritTemp = estCritTemp; // Start with our random guess
    
    for (let i = 1; i < results.length; i++) {
      const slope = (results[i-1].magnetization - results[i].magnetization) / 
                   (results[i].temperature - results[i-1].temperature);
      
      if (slope > maxSlope) {
        maxSlope = slope;
        finalCritTemp = (results[i-1].temperature + results[i].temperature) / 2;
      }
    }
    
    setCriticalTemp(finalCritTemp);
    return finalCritTemp;
  };
  
  // Improved temperature point generation with adaptive sampling
const generateTemperaturePoints = (
  initialEstimate = null, 
  minTemp, 
  maxTemp, 
  numPoints,
  previousResults = null
) => {
  // Convert to numbers to ensure correct type
  const min = parseFloat(minTemp);
  const max = parseFloat(maxTemp);
  const points = parseInt(numPoints);
  
  // If we have previous results, use them for adaptive sampling
  if (previousResults && previousResults.length > 5) {
    // Sort results by temperature
    const sortedResults = [...previousResults].sort((a, b) => a.temperature - b.temperature);
    
    // Calculate rate of change at each point
    const gradients = [];
    for (let i = 1; i < sortedResults.length; i++) {
      const prevPoint = sortedResults[i-1];
      const currPoint = sortedResults[i];
      
      const gradient = Math.abs((currPoint.magnetization - prevPoint.magnetization) / 
                              (currPoint.temperature - prevPoint.temperature));
      
      gradients.push({
        temperature: (prevPoint.temperature + currPoint.temperature) / 2,
        gradient
      });
    }
    
    // Find average gradient
    const avgGradient = gradients.reduce((sum, g) => sum + g.gradient, 0) / gradients.length;
    
    // Find regions with significant change (above average gradient)
    const significantGradients = gradients.filter(g => g.gradient > avgGradient * 0.8);
    
    if (significantGradients.length > 0) {
      // Define region of interest with some padding
      const minSignificantTemp = Math.max(
        min,
        Math.min(...significantGradients.map(g => g.temperature)) - 0.4
      );
      
      const maxSignificantTemp = Math.min(
        max,
        Math.max(...significantGradients.map(g => g.temperature)) + 0.4
      );
      
      // Allocate 80% of points to the significant region
      const significantPoints = Math.floor(points * 0.8);
      const remainingPoints = points - significantPoints;
      
      // Generate points for significant region (higher density)
      const significantStep = (maxSignificantTemp - minSignificantTemp) / 
                             (significantPoints - 1 || 1);
      const significantTemps = Array.from({ length: significantPoints }, (_, i) => 
        parseFloat((minSignificantTemp + i * significantStep).toFixed(3))
      );
      
      // Allocate fewer points to flat regions
      const fullRangeTemps = [];
      
      if (minSignificantTemp > min) {
        // Points for the lower flat region
        const lowerPoints = Math.ceil(remainingPoints / 2);
        const lowerStep = (minSignificantTemp - min) / (lowerPoints || 1);
        for (let i = 0; i < lowerPoints; i++) {
          fullRangeTemps.push(parseFloat((min + i * lowerStep).toFixed(3)));
        }
      }
      
      if (maxSignificantTemp < max) {
        // Points for the upper flat region
        const upperPoints = Math.floor(remainingPoints / 2);
        const upperStep = (max - maxSignificantTemp) / (upperPoints || 1);
        for (let i = 1; i <= upperPoints; i++) {
          fullRangeTemps.push(parseFloat((maxSignificantTemp + i * upperStep).toFixed(3)));
        }
      }
      
      // Combine and sort all temperatures
      const allTemps = [...significantTemps, ...fullRangeTemps].sort((a, b) => a - b);
      
      console.log("Using adaptive sampling with focus on significant gradients");
      return allTemps;
    }
  }
  
  // Default linear distribution if no adaptive sampling
  const step = (max - min) / (points - 1);
  return Array.from({ length: points }, (_, i) => 
    parseFloat((min + i * step).toFixed(3))
  );
};


  // Equilibrate and measure magnetization at one temperature
  const calculateMagnetizationAtTemperature = async (temperature, model) => {
    try {
      // Progress update for equilibration phase
      setProgress(prevProgress => prevProgress + 0.1);
      
      // Use standard Metropolis
      console.log(`Using Metropolis algorithm at T=${temperature}`);
      
      // Use user-specified equilibration steps without modification
      const userEquilibrationSteps = equilibrationSteps;
      
      // Run equilibration in chunks to keep UI responsive
      const chunkSize = 2000;
      for (let step = 0; step < userEquilibrationSteps; step += chunkSize) {
        if (abortController.current?.signal.aborted) {
          throw new Error('Calculation aborted');
        }
        
        const stepsToRun = Math.min(chunkSize, userEquilibrationSteps - step);
        model.simulationSteps(temperature, stepsToRun);
        
        // Allow UI to update
        await new Promise(r => setTimeout(r, 0));
      }
      
      // Measurement phase
      const magnetizationValues = [];
      const energyValues = [];
      
      // Take measurements with the specified steps between them
      for (let m = 0; m < measurementTrials; m++) {
        // Run simulation steps between measurements
        model.simulationSteps(temperature, measurementSteps);
        
        // Record measurements
        magnetizationValues.push(model.calculateAbsoluteMagnetization());
        
        // Record energy per spin
        energyValues.push(model.energy / model.numNodes);
        
        // Allow cancellation during measurement
        if (abortController.current?.signal.aborted) {
          throw new Error('Calculation aborted');
        }
      }
      
      // Calculate statistics from measurements
      console.log(`Collected ${magnetizationValues.length} measurements at T=${temperature.toFixed(3)}`);
      
      // Calculate mean magnetization and standard deviation
      const meanMag = magnetizationValues.reduce((sum, val) => sum + val, 0) / magnetizationValues.length;
      
      // Calculate standard deviation
      const squaredDiffs = magnetizationValues.map(val => (val - meanMag) ** 2);
      const variance = squaredDiffs.reduce((sum, val) => sum + val, 0) / magnetizationValues.length;
      const stdDev = Math.sqrt(variance);
      
      // Calculate energy average if available
      const meanEnergy = energyValues.length > 0 ? 
                        energyValues.reduce((sum, val) => sum + val, 0) / energyValues.length : 
                        null;
      
      return { 
        mean: meanMag, 
        stdDev: stdDev,
        errorHigh: meanMag + stdDev,
        errorLow: Math.max(0, meanMag - stdDev),
        energy: meanEnergy
      };
    } catch (error) {
      console.error(`Error at temperature ${temperature}:`, error);
      throw error;
    }
  };
  
const graphPreviewRef = useRef(null);

const startCalculation = async () => {
  if (isCalculating || !adjacencyMatrix) return;
  
  try {
    // Stop real-time simulation if active
    if (isRealTimeSimulating) {
      setIsRealTimeSimulating(false);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (isingModelRef.current) {
        isingModelRef.current = null;
      }
      setSpins([]);
      setMagnetization(0);
      setAbsoluteMagnetization(0);
      setEnergy(0);
      setShowSimulationView(false);
    }
    
    // IMPORTANT: Stop D3 force simulation to free up compute power
    console.log("Checking graphSimulationRef:", graphSimulationRef.current);
    if (graphSimulationRef.current) {
      console.log("Stopping D3 force simulation during calculation");
      graphSimulationRef.current.stop();
      console.log("D3 simulation stopped, alpha:", graphSimulationRef.current.alpha());
    } else {
      console.warn("graphSimulationRef.current is null - D3 simulation not stopped!");
    }
    
    // Set calculation state
    setIsCalculating(true);
    setProgress(0);

    // Clear previous data
    setMagnetizationData([]);
    setCriticalTemp(null);
    setTrialCurves([]);
    setAllTcEstimates([]);
    setCurrentTrialIndex(0);
    
    // Create new abort controller
    abortController.current = new AbortController();
    
    const startTime = Date.now();
    
    // Arrays to store results and T_c estimates from each trial
    const allTcEstimates = [];
    const colors = [
      '#8884d8', '#82ca9d', '#ff7300', '#0088fe', '#ff6b81', 
      '#00c49f', '#ffbb28', '#9482c4', '#3670a0', '#d13b40'
    ];
    
    // Get more colors if needed
    while (colors.length < measurementTrials) {
      colors.push(`hsl(${Math.random() * 360}, 70%, 60%)`);
    }
    
    // Run multiple complete, independent trials
    for (let trial = 0; trial < measurementTrials; trial++) {
      if (abortController.current?.signal.aborted) break;
      
      // Update current trial index
      setCurrentTrialIndex(trial);
      console.log(`Starting trial ${trial + 1} of ${measurementTrials}`);
      
      // Generate temperature points for this trial (always from high to low)
      const temperatures = Array.from({ length: tempPoints }, (_, i) => 
        tempMax - i * ((tempMax - tempMin) / (tempPoints - 1))
      );
      
      // Create a new Ising model for each trial
      console.log(`Creating Ising model for trial ${trial + 1}`);
      const model = new IsingModel(adjacencyMatrix);
      
      // Start with a fresh random spin configuration
      model.state = Array(model.numNodes).fill().map(() => 
        Math.random() < 0.5 ? 1 : -1
      );
      model.energy = model.calculateTotalEnergy();
      
      // Results for this specific trial
      const trialResults = [];
      
      // Calculate magnetization at each temperature for this trial
      for (let i = 0; i < temperatures.length; i++) {
        if (abortController.current?.signal.aborted) break;
        
        const temp = temperatures[i];
        
        // Update progress
        const overallProgress = (trial * temperatures.length + i) / 
                               (measurementTrials * temperatures.length);
        setProgress(overallProgress * 100);
        
        // Equilibrate the system at this temperature
        const userEquilibrationSteps = equilibrationSteps;
        const chunkSize = 2000;
        
        for (let step = 0; step < userEquilibrationSteps; step += chunkSize) {
          if (abortController.current?.signal.aborted) break;
          const stepsToRun = Math.min(chunkSize, userEquilibrationSteps - step);
          model.simulationSteps(temp, stepsToRun);
          await new Promise(r => setTimeout(r, 0));
        }
        
        // Take measurements
        const magnetizationValues = [];
        const energyValues = [];
        
        // Reduce samples for speed while maintaining accuracy
        const samplesPerTemp = 10;
        
        for (let m = 0; m < samplesPerTemp; m++) {
          model.simulationSteps(temp, measurementSteps);
          magnetizationValues.push(model.calculateAbsoluteMagnetization());
          energyValues.push(model.energy / model.numNodes);
          if (abortController.current?.signal.aborted) break;
        }
        
        // Calculate statistics from measurements
        const meanMag = magnetizationValues.reduce((sum, val) => sum + val, 0) / 
                       magnetizationValues.length;
        const squaredDiffs = magnetizationValues.map(val => (val - meanMag) ** 2);
        const variance = squaredDiffs.reduce((sum, val) => sum + val, 0) / 
                        magnetizationValues.length;
        const stdDev = Math.sqrt(variance);
        const meanEnergy = energyValues.reduce((sum, val) => sum + val, 0) / 
                          energyValues.length;
        
        // Calculate susceptibility: χ = N * (<M²> - <M>²) / T
        // This peaks at Tc and can help identify the transition
        const meanMagSquared = magnetizationValues.reduce((sum, val) => sum + val * val, 0) / 
                              magnetizationValues.length;
        const susceptibility = model.numNodes * (meanMagSquared - meanMag * meanMag) / temp;
        
        // Add data point for this temperature in this trial
        trialResults.push({
          temperature: temp,
          magnetization: meanMag,
          stdDev: stdDev,
          energy: meanEnergy,
          susceptibility: susceptibility
        });
        
        // Sort by temperature (low to high)
        const sortedResults = [...trialResults].sort((a, b) => 
          a.temperature - b.temperature
        );
        
        // Create a real-time updated curve for this trial - use functional update pattern
setTrialCurves(prevCurves => {
  const newCurves = [...prevCurves];
  newCurves[trial] = {
    id: trial,
    name: `Trial ${trial + 1}`,
    color: colors[trial % colors.length],
    data: sortedResults
  };
  return newCurves;
});
        
        // Small delay to allow UI updates
        await new Promise(r => setTimeout(r, 10));
      }
      
      // Estimate critical temperature for this trial
      if (trialResults.length > 5) {
        // Sort by temperature for proper analysis
        const sortedResults = [...trialResults].sort((a, b) => 
          a.temperature - b.temperature
        );
          
        const critTempResult = estimateCriticalTemperatureFromData(sortedResults);
        
        if (critTempResult.value !== null) {
          allTcEstimates.push(critTempResult.value);
          console.log(`Trial ${trial + 1} Tc estimate: ${critTempResult.value.toFixed(3)}`);
          
          // Update Tc estimates in real time
          setAllTcEstimates([...allTcEstimates, critTempResult.value]);
        }
      }
    }
    
    // All trials completed - calculate final statistics
    if (allTcEstimates.length > 0) {
      // Calculate average T_c and standard deviation
      const mean = allTcEstimates.reduce((sum, val) => sum + val, 0) / 
                  allTcEstimates.length;
      const variance = allTcEstimates.reduce((sum, val) => 
        sum + Math.pow(val - mean, 2), 0
      ) / allTcEstimates.length;
      const stdDev = Math.sqrt(variance);
      
      // Set critical temperature with standard deviation as the error
      setCriticalTemp(mean);
      setTcStandardDev(stdDev);
      
      console.log(`Average Tc: ${mean.toFixed(3)} ± ${stdDev.toFixed(3)}`);
      
      // Store average results for display
      setAverageCriticalTemp(mean);
    }
    
    // Create aggregated magnetization curve
    if (trialCurves.length > 0) {
      // Collect all temperature points across all trials
      const allTemps = new Set();
      trialCurves.forEach(trial => {
        trial.data.forEach(point => {
          allTemps.add(point.temperature);
        });
      });
      
      // Sort temperatures
      const sortedTemps = Array.from(allTemps).sort((a, b) => a - b);
      
      // For each temperature, collect data from all trials
      const aggregatedData = sortedTemps.map(temp => {
        const dataPoints = [];
        
        trialCurves.forEach(trial => {
          const point = trial.data.find(p => p.temperature === temp);
          if (point) {
            dataPoints.push(point.magnetization);
          }
        });
        
        // Only use temperatures that appear in all trials
        if (dataPoints.length === trialCurves.length) {
          const meanMag = dataPoints.reduce((sum, val) => sum + val, 0) / 
                         dataPoints.length;
          
          const variance = dataPoints.reduce((sum, val) => 
            sum + Math.pow(val - meanMag, 2), 0
          ) / dataPoints.length;
          
          const stdDev = Math.sqrt(variance);
          
          return {
            temperature: temp,
            magnetization: meanMag,
            stdDev: stdDev,
            errorHigh: meanMag + stdDev,
            errorLow: Math.max(0, meanMag - stdDev)
          };
        }
        
        return null;
      }).filter(Boolean);
      
      // Update magnetization data for final display
      setMagnetizationData(aggregatedData);
    }
    
    const endTime = Date.now();
    setCalculationTime((endTime - startTime) / 1000);
  } catch (error) {
    console.error('Calculation error:', error);
    setFileError(`Calculation failed: ${error.message}`);
  } finally {
    setIsCalculating(false);
    
    // Restart D3 force simulation after calculation completes
    if (graphSimulationRef.current) {
      console.log("Restarting D3 force simulation after calculation");
      // Restart with a gentle alpha to avoid jarring movement
      graphSimulationRef.current.alpha(0.3).restart();
    }
  }
};
  
  // Stop the calculation
  const stopCalculation = () => {
    if (abortController.current) {
      abortController.current.abort();
    }
    
    // Restart D3 force simulation when user manually stops calculation
    if (graphSimulationRef.current) {
      console.log("Restarting D3 force simulation after manual stop");
      graphSimulationRef.current.alpha(0.3).restart();
    }
  };
  
  // Estimate critical temperature from the magnetization data
// Replace the existing estimateCriticalTemperatureFromData function
const estimateCriticalTemperatureFromData = (data) => {
  // Ensure we have enough data points
  if (!data || data.length < 5) {
    console.error("Insufficient data for Tc estimation");
    return { value: null, error: 0 };
  }
  
  // Make a copy of data and ensure it's sorted by temperature (from low to high)
  const sortedData = [...data].sort((a, b) => a.temperature - b.temperature);
  
  // Check if data has valid magnetization values
  const hasValidData = sortedData.every(d => typeof d.magnetization === 'number' && !isNaN(d.magnetization));
  if (!hasValidData) {
    console.error("Invalid magnetization values in data");
    return { value: null, error: 0 };
  }
  
  // Apply adaptive smoothing - use smaller window for dense data, larger for sparse
  const dataRange = sortedData[sortedData.length - 1].temperature - sortedData[0].temperature;
  const dataDensity = sortedData.length / dataRange;
  
  // Adapt window size based on data density (more points = smaller window needed)
  const windowSize = Math.max(3, Math.min(5, Math.ceil(9 - dataDensity)));
  
  console.log(`Using smoothing window of ${windowSize} for ${sortedData.length} points over range ${dataRange}`);
  
  // Apply optimized smoothing to magnetization data
  const smoothedData = [];
  
  for (let i = 0; i < sortedData.length; i++) {
    if (i < Math.floor(windowSize/2) || i >= sortedData.length - Math.floor(windowSize/2)) {
      // For points at the edges, just copy the original data
      smoothedData.push({...sortedData[i]});
    } else {
      // For interior points, use Gaussian-weighted smoothing (better peak preservation)
      let sumMag = 0;
      let sumWeights = 0;
      const sigma = windowSize / 4; // Standard deviation of Gaussian
      
      for (let j = -Math.floor(windowSize/2); j <= Math.floor(windowSize/2); j++) {
        // Gaussian weighting preserves peaks better than triangular
        const weight = Math.exp(-(j * j) / (2 * sigma * sigma));
        sumMag += sortedData[i + j].magnetization * weight;
        sumWeights += weight;
      }
      
      const avgMag = sumMag / sumWeights;
      smoothedData.push({
        ...sortedData[i], 
        magnetization: avgMag,
        originalMag: sortedData[i].magnetization // Keep original for reference
      });
    }
  }
  
  // Calculate numerical derivative with improved method
  // Use central difference formula for interior points
  const derivatives = [];
  
  for (let i = 1; i < smoothedData.length - 1; i++) {
    // Central difference for better accuracy
    const slope = (smoothedData[i+1].magnetization - smoothedData[i-1].magnetization) / 
                 (smoothedData[i+1].temperature - smoothedData[i-1].temperature);
    
    derivatives.push({
      temperature: smoothedData[i].temperature,
      slope: -slope, // Negative because we want rate of decrease
      magValue: smoothedData[i].magnetization
    });
  }
  
  if (derivatives.length < 3) {
    console.error("Insufficient derivative points for Tc estimation");
    return { value: null, error: 0 };
  }
  
  // Find region with maximum slope (steepest change)
  let maxSlope = 0;
  let maxSlopeIndex = -1;
  
  for (let i = 0; i < derivatives.length; i++) {
    if (derivatives[i].slope > maxSlope) {
      maxSlope = derivatives[i].slope;
      maxSlopeIndex = i;
    }
  }
  
  if (maxSlopeIndex < 0) {
    console.error("Could not find maximum slope point");
    return { value: null, error: 0 };
  }
  
  // Initial Tc estimate is simply the temperature at max slope point
  let critTemp = derivatives[maxSlopeIndex].temperature;
  
  // Try to refine with quadratic interpolation if we have enough points around max
  if (maxSlopeIndex > 0 && maxSlopeIndex < derivatives.length - 1) {
    try {
      // Get 3 points around maximum for quadratic interpolation
      const x = [
        derivatives[maxSlopeIndex-1].temperature,
        derivatives[maxSlopeIndex].temperature,
        derivatives[maxSlopeIndex+1].temperature
      ];
      
      const y = [
        derivatives[maxSlopeIndex-1].slope,
        derivatives[maxSlopeIndex].slope,
        derivatives[maxSlopeIndex+1].slope
      ];
      
      // Fit quadratic: y = ax² + bx + c
      // Solving system of equations for a, b, c
      const x0_2 = x[0] * x[0];
      const x1_2 = x[1] * x[1];
      const x2_2 = x[2] * x[2];
      
      const det = (x0_2 * (x[1] - x[2])) - (x[0] * (x1_2 - x2_2)) + (x1_2 * x[2] - x2_2 * x[1]);
      
      if (Math.abs(det) > 1e-10) { // Avoid divide by zero
        const a = ((y[0] * (x[1] - x[2])) - (x[0] * (y[1] - y[2])) + (y[1] * x[2] - y[2] * x[1])) / det;
        const b = ((x0_2 * (y[1] - y[2])) - (y[0] * (x1_2 - x2_2)) + (x1_2 * y[2] - x2_2 * y[1])) / det;
        
        // For maximum, x = -b/(2a) if a < 0
        if (a < 0) {
          const refinedTemp = -b / (2 * a);
          
          // Only use refined value if it's within the range of the 3 points
          if (refinedTemp >= x[0] && refinedTemp <= x[2]) {
            critTemp = refinedTemp;
            console.log(`Refined Tc from ${derivatives[maxSlopeIndex].temperature} to ${critTemp} using quadratic interpolation`);
          }
        }
      }
    } catch (e) {
      console.error("Error in quadratic interpolation:", e);
      // Fall back to simpler estimate (already set above)
    }
  }
  
  // Calculate uncertainty using width of peak in derivative (FWHM method)
  let critTempError = 0.05; // Default fallback
  
  // Find half-max points to estimate peak width
  const halfMax = maxSlope / 2;
  let leftIndex = maxSlopeIndex;
  let rightIndex = maxSlopeIndex;
  
  // Search left for half-maximum
  while (leftIndex > 0 && derivatives[leftIndex].slope > halfMax) {
    leftIndex--;
  }
  
  // Search right for half-maximum
  while (rightIndex < derivatives.length - 1 && derivatives[rightIndex].slope > halfMax) {
    rightIndex++;
  }
  
  // FWHM is the temperature range at half-maximum
  if (rightIndex > leftIndex) {
    const fwhm = derivatives[rightIndex].temperature - derivatives[leftIndex].temperature;
    // Use FWHM as estimate of uncertainty (typical for peak measurements)
    critTempError = fwhm / 2;
    console.log(`Estimated Tc uncertainty from peak width: ±${critTempError.toFixed(3)}`);
  }
  
  // Return both the value and error estimate
  return { value: critTemp, error: critTempError };
};
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, []);
  
  // Load default graph on initial render
  useEffect(() => {
    if (!isGraphLoaded) {
      loadExampleGraph('grid');
    }
  }, []);
  // Add this function near other utility functions like stopCalculation
const optimizeForCalculation = () => {
  console.log("Optimizing performance for calculation...");
  
  // Force stop any D3 interactions
  if (svgRef.current) {
    // Remove all zoom behaviors
    d3.select(svgRef.current).on('.zoom', null);
    
    // Remove all event listeners from nodes
    d3.select(svgRef.current).selectAll('.node').on('*', null);
    d3.select(svgRef.current).selectAll('.link').on('*', null);
    
    console.log("D3 event listeners cleared");
  }
  
  // Switch to non-interactive view
  if (isRealTimeSimulating) {
    // Stop simulation explicitly
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    setIsRealTimeSimulating(false);
    setShowSimulationView(false);
  }
};

  return (
    <div className="flex flex-col w-full bg-white p-4 rounded-lg">
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        {/* Lattice selection and custom upload */}
        <div className="bg-gray-100 p-3 rounded-lg w-full">
          <div className="flex flex-col gap-4">
          <div className="flex-1">
          <h3 className="font-bold mb-2">Example lattices:</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => loadExampleGraph('grid')}
              className="p-2 border rounded bg-blue-500 text-white hover:bg-blue-600 flex-1"
              disabled={isCalculating}
            >
              Square
            </button>
            <button
              onClick={() => loadExampleGraph('triangular')}
              className="p-2 border rounded bg-blue-500 text-white hover:bg-blue-600 flex-1"
              disabled={isCalculating}
            >
              Triangle
            </button>
            <button
              onClick={() => loadExampleGraph('hexagonal')}
              className="p-2 border rounded bg-blue-500 text-white hover:bg-blue-600 flex-1"
              disabled={isCalculating}
            >
              Honeycomb
            </button>

            <button
            onClick={() => loadExampleGraph('kagome')}
            className="p-2 border rounded bg-blue-500 text-white hover:bg-blue-600 flex-1"
            disabled={isCalculating}
            >
              Kagome
            </button>
            <button
              onClick={() => loadExampleGraph('star')}
              className="p-2 border rounded bg-blue-500 text-white hover:bg-blue-600 flex-1"
              disabled={isCalculating}
            >
              Star
            </button>
            <button
              onClick={() => loadExampleGraph('ruby')}
              className="p-2 border rounded bg-blue-500 text-white hover:bg-blue-600 flex-1"
              disabled={isCalculating}
            >
              Ruby
            </button>
          </div>
        </div>
            
            <div className="flex-1 mt-4 md:mt-0">
              <h3 className="font-bold mb-2">Upload adjacency matrix of a lattice</h3>
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="border p-2 flex-1"
                  disabled={isCalculating}
                />
                <div className="text-xs text-gray-500 whitespace-nowrap">
                  csv format: row,column,value (0 or 1)
                </div>
              </div>
              
              {/* Graph stats - moved here */}
              {isGraphLoaded && (
                <div className="grid grid-cols-3 gap-2 p-1.5 bg-gray-100 rounded text-xs">
                  <div className="flex flex-col">
                    <span className="font-bold text-gray-700">Nodes</span>
                    <span>N = {graphStats.nodes}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="font-bold text-gray-700">Edges</span>
                    <span>E = {graphStats.edges}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="font-bold text-gray-700">Average coordination number</span>
                    <span>2E/N = {(2 * graphStats.edges / graphStats.nodes).toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Error display area */}
      {fileError && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <strong className="font-bold">Error:</strong>
          <span className="block sm:inline"> {fileError}</span>
          <button 
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setFileError(null)}
          >
            <span className="text-xl">&times;</span>
          </button>
        </div>
      )}
      
      {/* Graph preview and stats with dual view */}
{isGraphLoaded && (
  <div className="flex flex-col md:flex-row gap-4">
    {/* Left side: Graph display area */}
    <div className="w-full md:w-3/4 p-3 rounded-lg">
      <div className="w-full bg-white rounded shadow-md">
        {!showSimulationView ? (
          <GraphPreview 
            adjacencyMatrix={adjacencyMatrix}
            spins={spins}
            onSvgRef={handleSvgRef}
            simulationRef={graphSimulationRef}
            isCalculating={isCalculating}
            onStateChange={setGraphState}
          />
        ) : (
          <StaticIsingView 
            adjacencyMatrix={adjacencyMatrix}
            spins={spins}
            positions={nodePositions}
            nodeRadius={currentNodeRadius}
          />
        )}
      </div>
    </div>
    
    {/* Right side: Real-time Ising Model Simulation controls */}
    <div className="w-full md:w-1/4 bg-gray-100 p-3 rounded-lg flex flex-col gap-4">      
      <div className="flex flex-col gap-3">
        <div>
          <h4 className="font-semibold mb-1">Temperature Control</h4>
          <div className="flex flex-col items-start gap-1">
            <input 
              type="range" 
              min="0.5" 
              max="5.0" 
              step="0.1" 
              value={simulationTemp}
              onChange={handleSimulationTempChange}
              className="w-full"
              disabled={isCalculating}
            />
            <div className="flex justify-between w-full text-xs">
              <span>Cold (Ordered)</span>
              <span className="font-bold">{simulationTemp.toFixed(1)} <i>J</i>/<i>k</i><sub>B</sub></span>
              <span>Hot (Disordered)</span>
            </div>
          </div>
        </div>
        
        <div>
          <h4 className="font-semibold mb-1">Time Scale (Flips per frame)</h4>
          <div className="flex flex-col items-start gap-1">
            <input 
              type="range" 
              min="1" 
              max="500" 
              step="10" 
              value={timeScale}
              onChange={handleTimeScaleChange}
              className="w-full"
              disabled={isCalculating}
            />
            <div className="flex justify-between w-full text-xs">
              <span>Slower</span>
              <span className="font-bold">{timeScale}</span>
              <span>Faster</span>
            </div>
          </div>
        </div>
        
        <div>
</div>
        
<div>
  <div className="text-xs text-purple-600 font-semibold text-center mb-1 border-2 border-purple-600 rounded-lg p-2">
    <strong>NOTE:</strong> T<sub>c</sub> displayed here might be inaccurate at first try, press refresh or use M vs T curve below
  </div>
  <table className="w-full border-collapse rounded-lg overflow-hidden shadow-md">
    <tbody>
      <tr className={`transition-colors duration-300 ${
        tcCalculationStatus === 'calculating' ? 'bg-blue-600 text-white' :
        tcCalculationStatus === 'success' || criticalTemp ? 'bg-green-500 text-white' : 
        tcCalculationStatus === 'error' ? 'bg-red-600 text-white' : ''
      }`}>
        <th className="border px-3 py-3 text-center text-lg font-bold">T<sub>c</sub></th>
        <td className="border px-3 py-3 text-lg text-center">
          <div className="flex items-center justify-center">
            {tcCalculationStatus === 'calculating' ? (
              <span className="text-white flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Calculating...
              </span>
            ) : criticalTemp ? (
              <span className="font-bold">≈{(() => {
                const formatted = formatTcWithError(criticalTemp, tcStandardDev);
                return tcStandardDev ? formatted.tcStr : criticalTemp.toFixed(3);
              })()}</span>
            ) : (
              "Unknown"
            )}
            <button 
              onClick={updateAverageCriticalTemperature}
              className="ml-3 text-white hover:text-gray-200"
              disabled={tcCalculationStatus !== null}
              title="Recalculate T_c using high-temperature sampling"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </td>
      </tr>
      <tr className={`transition-colors duration-300 ${
        getPhaseDescription() === "Ordered (Ferromagnetic)" ? 'bg-blue-600 text-white' :
        getPhaseDescription() === "Near Critical Point" ? 'bg-yellow-500 text-white' :
        getPhaseDescription() === "Disordered (Paramagnetic)" ? 'bg-red-600 text-white' : ''
      }`}>
        <th className="border px-3 py-3 text-center text-lg font-bold">Phase</th>
        <td className="border px-3 py-3 text-lg text-center font-bold">
          {getPhaseDescription()}
        </td>
      </tr>
    </tbody>
  </table>
</div>
        
        <div className="flex flex-col gap-2">
          <button
            onClick={toggleRealTimeSimulation}
            className={`px-4 py-2 font-bold text-white rounded ${isRealTimeSimulating ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}`}
            disabled={isCalculating}
          >
            {isRealTimeSimulating ? 'Stop Simulation' : 'Simulate Magnet'}
          </button>
          
          <button
            onClick={resetSpins}
            className="px-4 py-2 font-bold bg-yellow-500 text-white rounded hover:bg-yellow-600"
            disabled={isCalculating}
          >
            Randomize Spins
          </button>
          
          <button
            onClick={() => setAllSpins(1)}
            className="px-4 py-2 font-bold bg-black text-white rounded hover:bg-gray-800"
            disabled={isCalculating}
          >
            All Up (Black)
          </button>
          
          <button
            onClick={() => setAllSpins(-1)}
            className="px-4 py-2 font-bold bg-white text-black border-2 border-black rounded hover:bg-gray-100"
            disabled={isCalculating}
          >
            All Down (White)
          </button>
          
          {/* Graph layout controls */}
          <div className="mt-4 pt-4 border-t border-gray-300">
            <h4 className="font-semibold mb-2 text-sm">Graph Layout Controls</h4>
            <div className="grid grid-cols-2 gap-2">
              <button 
                onClick={() => {
                  // Access the GraphPreview's refreshGraph function via ref
                  if (svgRef.current) {
                    const event = new CustomEvent('refreshGraph');
                    svgRef.current.dispatchEvent(event);
                  }
                }}
                className="px-4 py-2 font-bold bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Jerk the graph to untangle overlapping nodes. Spam if necessary!"
                disabled={isCalculating || isRealTimeSimulating}
              >
                Untangle
              </button>
              
              <button 
                onClick={() => {
                  if (svgRef.current) {
                    const event = new CustomEvent('toggleStretch');
                    svgRef.current.dispatchEvent(event);
                  }
                }}
                className={`px-4 py-2 font-bold rounded disabled:opacity-50 disabled:cursor-not-allowed ${
                  graphState.isStretched 
                    ? 'bg-orange-500 text-white hover:bg-orange-600' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                title="Toggle between stretched and normal layout"
                disabled={isCalculating || isRealTimeSimulating}
              >
                {graphState.isStretched ? 'Stretched' : 'Stretch'}
              </button>
              
              <button
                onClick={() => {
                  if (svgRef.current) {
                    const event = new CustomEvent('stopStretch');
                    svgRef.current.dispatchEvent(event);
                  }
                }}
                className={`px-4 py-2 font-bold rounded disabled:opacity-50 disabled:cursor-not-allowed ${
                  graphState.isPaused 
                    ? 'bg-yellow-500 text-white hover:bg-yellow-600' 
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
                title={graphState.isPaused ? 'Resume animation' : 'Pause animation'}
                disabled={isCalculating || isRealTimeSimulating}
              >
                {graphState.isPaused ? 'Resume' : 'Pause'}
              </button>
              
              <button
                onClick={() => {
                  if (svgRef.current) {
                    const event = new CustomEvent('fitToScreen');
                    svgRef.current.dispatchEvent(event);
                  }
                }}
                className="px-4 py-2 font-bold bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zoom to fit all nodes"
                disabled={isCalculating || isRealTimeSimulating}
              >
                Fit
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
)}
      
      {/* Temperature and Monte Carlo parameters */}
      <div className="bg-gray-100 p-3 rounded-lg mb-4 mt-0">
  <h3 className="font-bold mb-2">Magnetization Vs Temperature</h3>
  
  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
    <div>
      <h4 className="font-semibold mb-1">Equilibration Steps:</h4>
      <input 
        type="number" 
        min="1000" 
        max="100000" 
        step="1000" 
        value={equilibrationSteps}
        onChange={(e) => setEquilibrationSteps(parseInt(e.target.value))}
        className="w-full p-2 border rounded"
        disabled={isCalculating}
      />
      <div className="text-xs text-gray-500 mt-1">
        Steps to reach equilibrium (higher = more accurate, slower calculation)
      </div>
    </div>
    
    <div>
      <h4 className="font-semibold mb-1">Measurement Steps:</h4>
      <input 
        type="number" 
        min="100" 
        max="20000" 
        step="100" 
        value={measurementSteps}
        onChange={(e) => setMeasurementSteps(parseInt(e.target.value))}
        className="w-full p-2 border rounded"
        disabled={isCalculating}
      />
      <div className="text-xs text-gray-500 mt-1">
        Steps between measurements (higher = better decorrelation)
      </div>
    </div>
    
    <div>
      <h4 className="font-semibold mb-1">Measurement Trials:</h4>
      <input 
        type="number" 
        min="1" 
        max="1000" 
        step="1" 
        value={measurementTrials}
        onChange={(e) => setMeasurementTrials(parseInt(e.target.value))}
        className="w-full p-2 border rounded"
        disabled={isCalculating}
      />
      <div className="text-xs text-gray-500 mt-1">
        Number of measurements averaged per temperature point
      </div>
    </div>
    
    <div>
      <h4 className="font-semibold mb-1">Temperature Range:</h4>
      <div className="flex items-center gap-2">
        <input 
          type="number" 
          min="0.1" 
          max="5" 
          step="0.1" 
          value={tempMin}
          onChange={(e) => setTempMin(parseFloat(e.target.value))}
          className="p-2 border rounded w-14"
          disabled={isCalculating}
        />
        <span>to</span>
        <input 
          type="number" 
          min="0.5"
          max="10" 
          step="0.1" 
          value={tempMax}
          onChange={(e) => setTempMax(parseFloat(e.target.value))}
          className="p-2 border rounded w-14"
          disabled={isCalculating}
        />
        <span>Pts:</span>
        <input 
          type="number" 
          min="5" 
          max="50" 
          step="1" 
          value={tempPoints}
          onChange={(e) => setTempPoints(parseInt(e.target.value))}
          className="p-2 border rounded w-14"
          disabled={isCalculating}
        />
      </div>
    </div>
  </div>
</div>
      
      {/* Calculation control */}
      <div className="flex justify-center mb-4">
        {!isCalculating ? (
          <button
          onClick={startCalculation}
          className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          disabled={!isGraphLoaded || isCalculating}
        >
          Plot Magnetization vs Temperature
        </button>
        ) : (
          <button
            onClick={stopCalculation}
            className="px-6 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Stop Calculation
          </button>
        )}
      </div>
      
      {/* Progress bar */}
      {isCalculating && (
        <div className="mb-4">
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div 
              className="bg-blue-600 h-4 rounded-full transition-all duration-300" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="text-center mt-2">
            Calculating... {Math.round(progress)}% complete
            {firstRunComplete && useTwoStageSampling && initialTcEstimate && (
              <span> (refining around Tc≈{initialTcEstimate.toFixed(2)})</span>
            )}
          </div>
        </div>
      )}
      
{/* Results graph */}
{trialCurves.length > 0 && (
  <div className="bg-white rounded-lg shadow-md p-4 mb-4">
    <h3 className="font-bold mb-3">Magnetization vs Temperature</h3>
    
    {criticalTemp && (
      <div className="mb-3 p-2 bg-yellow-100 rounded">
        <span>Estimated critical temperature: T<sub>c</sub> = {(() => {
          const formatted = formatTcWithError(criticalTemp, tcStandardDev);
          return `${formatted.tcStr} ± ${formatted.errorStr}`;
        })()} <i>J</i>/<i>k</i><sub>B</sub></span>
      </div>
    )}
    
    <div className="h-96">
      <ResponsiveContainer width="100%" height="100%">
      <LineChart
        margin={{ top: 20, right: 30, left: 70, bottom: 30 }}
      >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="temperature" 
            type="number"
            domain={[tempMin, tempMax]}
            tickCount={7}
          >
            <Label value="Temperature (J/k_B)" position="insideBottomRight" offset={40} />
          </XAxis>
          <YAxis domain={[0, 1.05]}>
            <Label value="Magnetization |M| (per spin)" position="left" angle={-90} offset={0} style={{ textAnchor: 'middle' }} />
          </YAxis>
          <Tooltip 
            formatter={(value, name) => {
              if (name.startsWith('Trial')) return [value.toFixed(4), name];
              return [value, name];
            }}
            labelFormatter={(value) => `Temperature: ${value}`}
          />
          <Legend 
          layout="horizontal" 
          verticalAlign="bottom"
          align="center"
          wrapperStyle={{ 
            paddingTop: 10,
            paddingBottom: 5,
            marginTop: 20,
            backgroundColor: '#f8f8f8', 
            border: '1px solid #eee',
            borderRadius: 3
          }}
        />
          
          {/* Multiple lines for each trial */}
          {trialCurves.map((trial, index) => {
            console.log(`Rendering trial ${index + 1} with ${trial?.data?.length || 0} data points`);
            return (
              <Line 
                key={index}
                name={`Trial ${index + 1}`}
                data={trial.data}
                type="monotone" 
                dataKey="magnetization" 
                stroke={trial.color || '#8884d8'}
                dot={{ r: 3 }} 
                isAnimationActive={false}
                connectNulls={true}
              />
            );
          })}
          
          {/* Critical temperature line */}
          {criticalTemp && (
            <ReferenceLine
              x={criticalTemp}
              stroke="red"
              strokeDasharray="5 5"
              label={{ 
                value: (() => {
                  const formatted = formatTcWithError(criticalTemp, tcStandardDev);
                  return `Tc = ${formatted.tcStr} ± ${formatted.errorStr}`;
                })(),
                position: 'top',
                fill: 'red'
              }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
    
    {calculationTime > 0 && (
      <div className="text-sm text-gray-600 text-right mt-2">
        Calculation time: {calculationTime.toFixed(1)} seconds
      </div>
    )}
  </div>
)}
        {/* Information Section */}
      <div className="bg-gray-100 p-4 rounded-lg mt-4 text-center">
          <p className="flex flex-wrap justify-center items-center gap-3 text-base font-bold">
            <a 
              href="https://dvdjsp.github.io./" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-xl font-bold text-purple-700 hover:text-purple-900 hover:underline"
            >
              Davidson Joseph
            </a>
            <span className="text-gray-500 text-lg">+</span>
            <code className="bg-gray-200 text-gray-900 px-2 py-1 rounded-lg text-base font-bold font-mono">
              Claude 4.5
            </code>
          </p>
        </div>
      </div>
  );
}

export default MagnetizationCalculator;